<template>
  <v-container fluid class="page">
    <div class="page-hero">
      <div>
        <h2 class="d-flex align-center">{{ collectionsOnly ? 'Cobranzas' : (viewMode === 'supplier' ? 'Proveedores' : 'Clientes') }} <HelpHint :text="collectionsOnly ? 'Consultá saldos, aplicá recibos y seguí la cuenta corriente del cliente.' : (viewMode === 'supplier' ? 'Gestioná cuentas de proveedores, saldos y movimientos de pagos/cargos.' : 'Gestioná cuentas de clientes, límite de crédito, saldos y movimientos.')"/></h2>
        <p>{{ collectionsOnly ? 'Registrá cobros y consultá comprobantes pendientes por cliente.' : (viewMode === 'supplier' ? 'Administrá proveedores, precios y pagos.' : 'Administrá clientes con crédito, lista de precios asociada y condiciones de pago.') }}</p>
      </div>
      <div class="d-flex" style="gap: 12px; align-items: center;">
        <v-btn color="primary" outlined :loading="loading" @click="loadAll">
          <v-icon left>mdi-refresh</v-icon> Recargar
        </v-btn>
        <v-btn v-if="!collectionsOnly" color="success" class="px-5 font-weight-bold elevation-2" @click="openCreateDialog">
          <v-icon left>mdi-plus-circle</v-icon>
          {{ viewMode === 'supplier' ? 'Nuevo Proveedor' : 'Nuevo Cliente' }}
        </v-btn>
      </div>
    </div>

    <v-alert v-if="error" type="error" dense outlined class="mb-4">{{ error }}</v-alert>
    <v-alert v-if="success" type="success" dense outlined class="mb-4">{{ success }}</v-alert>
    <v-alert v-if="pendingReceipt" type="warning" outlined class="mb-4">
      <strong>Hay un cobro pendiente de confirmar.</strong> Se perdió la respuesta para un cobro de $ {{ formatAmount(pendingReceipt.payload.amount) }}. Recuperalo antes de registrar otro para evitar duplicados.
      <div class="caption mt-1">Si habías seleccionado un archivo, revisá el recibo y adjuntalo nuevamente cuando aparezca.</div>
      <v-btn color="warning" class="mt-2" small :loading="recoveringReceipt" @click="recoverPendingReceipt">Recuperar cobro</v-btn>
    </v-alert>

    <v-card outlined class="page-card mb-4">
      <v-card-title class="subtitle-1">Buscar</v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="5"><v-autocomplete
            v-model="selectedSuggestion" :search-input.sync="suggestionText" :items="customerSuggestions"
            item-text="name" item-value="id" return-object no-filter clearable hide-no-data
            :loading="suggestionsLoading" label="Código, nombre, CUIT o email" outlined dense
            hint="Escribí al menos 2 caracteres para ver sugerencias" persistent-hint
            @change="chooseSuggestion" @keydown.enter="searchTyped">
            <template v-slot:item="{ item }"><v-list-item-content><v-list-item-title>{{ item.name }}</v-list-item-title><v-list-item-subtitle>{{ item.code }} · {{ item.taxId || 'Sin CUIT' }}</v-list-item-subtitle></v-list-item-content></template>
            <template v-slot:selection="{ item }">{{ item.name }} · {{ item.code }}</template>
          </v-autocomplete><div v-if="suggestionsError" class="caption error--text">No se pudieron cargar sugerencias. Podés usar Buscar.</div></v-col>
          <v-col cols="12" md="3">
            <v-select v-model="filters.priceListId" :items="priceListOptions" item-text="label" item-value="id" clearable label="Lista de precios" outlined dense />
          </v-col>
          <v-col cols="12" md="2">
            <v-select v-model="filters.isActive" :items="activeOptions" item-text="text" item-value="value" label="Estado" outlined dense />
          </v-col>
          <v-col cols="12" md="2" class="d-flex pt-2 pt-md-6">
            <v-btn color="primary" block height="40" :loading="loading" @click="fetchRows" class="elevation-2 font-weight-bold">
              <v-icon left>mdi-magnify</v-icon> Buscar
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Dialogo Modal de Formulario de Creación/Edición -->
    <v-dialog v-model="formDialog" max-width="900px" persistent>
      <v-card class="rounded-xl overflow-hidden shadow-2xl">
        <!-- Encabezado Sleek Gradiente -->
        <v-card-title class="headline white--text d-flex justify-space-between align-center px-6 py-4" style="background: linear-gradient(135deg, #1e293b, #334155);">
          <div class="d-flex align-center">
            <v-icon dark left class="mr-2">{{ viewMode === 'supplier' ? 'mdi-truck-delivery-outline' : 'mdi-account-outline' }}</v-icon>
            <span class="font-weight-bold" style="letter-spacing: -0.01em;">
              {{ form.id ? (viewMode === 'supplier' ? 'Editar Proveedor' : 'Editar Cliente') : (viewMode === 'supplier' ? 'Nuevo Proveedor' : 'Nuevo Cliente') }}
            </span>
          </div>
          <v-btn icon dark small @click="closeFormDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-6 mt-2">
          <v-row dense>
            <v-col cols="12" md="3"><v-text-field v-model="form.code" label="Código" outlined dense /></v-col>
            <v-col cols="12" md="5"><v-text-field v-model="form.name" label="Nombre / Razón social" outlined dense /></v-col>
            <v-col cols="12" md="4"><v-text-field v-model="form.tradeName" label="Nombre fantasía" outlined dense /></v-col>
            <v-col cols="12" md="4"><v-text-field v-model="form.taxId" label="CUIT / Identificación fiscal" outlined dense /></v-col>
            <v-col cols="12" md="4"><v-text-field v-model="form.iibbProvince" label="Provincia IIBB" outlined dense /></v-col>
            <v-col cols="12" md="4"><v-text-field v-model="form.brokerName" label="Corredor" outlined dense /></v-col>

            <v-col cols="12" md="4"><v-text-field v-model="form.phone" label="Teléfono" outlined dense /></v-col>
            <v-col cols="12" md="4"><v-text-field v-model="form.email" label="Email" outlined dense /></v-col>
            <v-col cols="12" md="4"><v-text-field v-model="form.address" label="Dirección" outlined dense /></v-col>
            <v-col cols="12" md="6"><v-text-field v-model="form.deliveryAddress" label="Lugar de entrega (si difiere)" outlined dense /></v-col>
            <v-col cols="12" md="3"><v-text-field v-model="form.contactTimeWindow" label="Horario de contacto (ej 08:00-12:00)" outlined dense /></v-col>
            <v-col cols="12" md="3"><v-text-field v-model="form.paymentCondition" label="Condición de pago" outlined dense /></v-col>

            <v-col cols="12" md="4">
              <v-select v-model="form.priceListId" :items="priceListOptions" item-text="label" item-value="id" clearable label="Lista de precios asignada" outlined dense />
            </v-col>
            <v-col cols="12" md="2">
              <v-select v-model="form.purchaseInvoiceType" :items="purchaseInvoiceTypeOptions" item-text="text" item-value="value" clearable label="Comprobante a emitir" outlined dense>
                <template v-slot:append>
                  <v-tooltip bottom max-width="250">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon small color="blue-grey lighten-2" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                    </template>
                    <span>Letra impositiva predeterminada para facturar a este cliente ante AFIP (A para Responsables Inscriptos, B o C para Consumidores Finales/Exentos).</span>
                  </v-tooltip>
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" md="2">
              <v-text-field v-model.number="form.creditLimit" type="number" min="0" step="0.01" label="Límite de crédito" outlined dense>
                <template v-slot:append>
                  <v-tooltip bottom max-width="250">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon small color="blue-grey lighten-2" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                    </template>
                    <span>Monto máximo que el cliente puede adeudar. Si el saldo deudor supera este límite, el mostrador no le permitirá emitir pedidos a crédito.</span>
                  </v-tooltip>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="2">
              <v-text-field v-model.number="form.balance" type="number" step="0.01" label="Saldo actual" outlined dense>
                <template v-slot:append>
                  <v-tooltip bottom max-width="250">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon small color="blue-grey lighten-2" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                    </template>
                    <span>Balance acumulado actual del cliente. Si es positivo, nos adeuda dinero; si es negativo, posee saldo a favor.</span>
                  </v-tooltip>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="2"><v-text-field v-model.number="form.paymentTermsDays" type="number" min="0" step="1" label="Plazo (días)" outlined dense /></v-col>
            <v-col cols="12" md="2"><v-switch v-model="form.isActive" inset label="Activa" /></v-col>
            <v-col cols="12" md="2"><v-switch v-model="form.purchasedAtCounter" inset label="Compra mostrador" /></v-col>
            <v-col v-if="form.accountType === 'customer'" cols="12" md="2"><v-select v-model="form.clientType" :items="clientTypeOptions" item-text="text" item-value="value" label="Tipo cliente" outlined dense /></v-col>

            <v-col cols="12" md="4"><v-text-field v-model.number="form.habitualBonusPercent" type="number" step="0.0001" label="Bonificación habitual (%)" outlined dense /></v-col>
            <v-col cols="12" md="4"><v-text-field v-model.number="form.brokerBonusScalePercent" type="number" step="0.0001" label="Escala bonif corredor (%)" outlined dense /></v-col>
            <v-col cols="12" md="4"><v-text-field v-model.number="form.sellerCommissionPercent" type="number" step="0.0001" label="Comisión vendedor (%)" outlined dense /></v-col>

            <v-col cols="12"><v-text-field v-model="form.notes" label="Observaciones" outlined dense /></v-col>
            <v-col cols="12"><v-text-field v-model="form.internalNotes" label="Observaciones internas" outlined dense /></v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="px-6 pb-6 pt-0 d-flex justify-end gap-2" style="gap: 8px;">
          <v-btn text color="secondary" @click="closeFormDialog">Cancelar</v-btn>
          <v-btn color="success" class="px-6 rounded-lg font-weight-bold" :loading="saving" @click="saveRow">
            <v-icon left>mdi-check-circle-outline</v-icon> {{ form.id ? "Guardar Cambios" : "Crear" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card outlined class="page-card">
      <v-card-title class="subtitle-1">Listado</v-card-title>
      <v-data-table :headers="headers" :items="rows" :loading="loading" item-key="id" dense>
        <template v-slot:item.priceList="{ item }">
          <span>{{ item.priceList ? `${item.priceList.code} - ${item.priceList.name}` : "—" }}</span>
        </template>
        <template v-slot:item.accountType="{ item }">
          <span>{{ item.accountType === "supplier" ? "Proveedor" : "Cliente" }}</span>
        </template>
        <template v-slot:item.clientType="{ item }">
          <v-chip x-small :color="item.clientType === 'CC' ? 'success' : 'primary'" dark label>
            {{ item.clientType === 'CC' ? 'CC' : 'CM' }}
          </v-chip>
        </template>
        <template v-slot:item.isActive="{ item }">
          <span :class="['pill', item.isActive ? 'pill--success' : 'pill--warn']">{{ item.isActive ? "Activa" : "Inactiva" }}</span>
        </template>
        <template v-slot:item.balance="{ item }"><span :class="balanceClass(item.balance)">{{ balanceText(item.balance) }}: $ {{ formatAmount(Math.abs(Number(item.balance || 0))) }}</span></template>
        <template v-slot:item.openDebt="{ item }"><span :class="Number(item.openDebt || 0) > 0 ? 'font-weight-bold orange--text text--darken-3' : 'text--secondary'">$ {{ formatAmount(item.openDebt) }}</span></template>
        <template v-slot:item.overdueDebt="{ item }"><span :class="Number(item.overdueDebt || 0) > 0 ? 'font-weight-bold red--text text--darken-2' : 'text--secondary'">$ {{ formatAmount(item.overdueDebt) }}</span></template>
        <template v-slot:item.actions="{ item }">
          <div class="d-flex align-center flex-nowrap" style="gap:4px">
            <v-btn v-if="item.accountType === 'customer'" small color="primary" depressed @click="openCommercialHistory(item)">Ver ficha</v-btn>
            <v-btn v-else small color="primary" depressed @click="openMovements(item)">Movimientos</v-btn>
            <v-btn v-if="item.accountType === 'customer'" small text color="success" @click="openPaymentModal(item)">Cobrar</v-btn>
            <v-btn v-else small text color="success" @click="openPaymentModal(item)">Pagar</v-btn>
            <v-menu bottom left><template v-slot:activator="{ on, attrs }"><v-btn icon small aria-label="Más acciones" v-bind="attrs" v-on="on"><v-icon>mdi-dots-vertical</v-icon></v-btn></template>
              <v-list dense><v-list-item @click="editRow(item)"><v-list-item-title>Editar datos</v-list-item-title></v-list-item>
                <v-list-item v-if="item.accountType === 'customer'" @click="openMovements(item)"><v-list-item-title>Ajuste manual y movimientos</v-list-item-title></v-list-item>
                <v-list-item v-if="item.accountType === 'customer'" @click="openDepositModal(item)"><v-list-item-title>Ingreso a cuenta</v-list-item-title></v-list-item>
              </v-list>
            </v-menu>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <v-card outlined class="page-card mt-4">
      <v-card-title class="subtitle-1 d-flex justify-space-between align-center">
        <span>Resumen de cuentas corrientes</span>
        <v-btn color="primary" small :loading="reportLoading" @click="fetchBalancesReport">Actualizar reporte</v-btn>
      </v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="4">
            <div class="caption text--secondary text-uppercase">Clientes activos</div>
            <div class="subtitle-1 font-weight-bold">{{ report.customers.summary.count }} cuentas</div>
            <div :class="balanceClass(report.customers.summary.totalBalance)">{{ balanceText(report.customers.summary.totalBalance) }}: {{ formatAmount(Math.abs(Number(report.customers.summary.totalBalance || 0))) }}</div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="caption text--secondary text-uppercase">Proveedores activos</div>
            <div class="subtitle-1 font-weight-bold">{{ report.suppliers.summary.count }} cuentas</div>
            <div :class="balanceClass(report.suppliers.summary.totalBalance)">{{ balanceText(report.suppliers.summary.totalBalance) }}: {{ formatAmount(Math.abs(Number(report.suppliers.summary.totalBalance || 0))) }}</div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="caption text--secondary text-uppercase">Cuentas activas</div>
            <div class="subtitle-1 font-weight-bold">{{ report.overall.count }} cuentas</div>
            <div :class="balanceClass(report.overall.totalBalance)">{{ balanceText(report.overall.totalBalance) }} neto: {{ formatAmount(Math.abs(Number(report.overall.totalBalance || 0))) }}</div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card v-if="selectedAccountId && !historyDialog" outlined class="page-card mt-4">
      <v-card-title class="subtitle-1 d-flex justify-space-between align-center">
        <div>
          <div>Cuenta corriente</div>
          <div class="caption text--secondary">{{ selectedAccountLabel }}</div>
        </div>
        <div class="d-flex align-center" style="gap: 12px;">
          <div class="text-right">
            <div class="caption text--secondary">Saldo actual</div>
            <div :class="balanceClass(selectedAccountBalance)">{{ balanceText(selectedAccountBalance) }}: {{ formatAmount(Math.abs(Number(selectedAccountBalance || 0))) }}</div>
          </div>
          <v-btn text small color="primary" :loading="movementsLoading" @click="fetchMovements">Recargar</v-btn>
        </div>
      </v-card-title>
      <v-card-text>
        <v-alert v-if="selectedAccountBalance < 0" dense outlined type="success" class="mb-4">
          <strong>Crédito disponible para próximas compras: {{ formatAmount(Math.abs(selectedAccountBalance)) }}</strong><br />
          Este dinero queda a cuenta del cliente. En una venta futura, elegí “Usar saldo a favor / Cuenta corriente” para aplicarlo.
        </v-alert>
        <v-alert dense outlined type="info" class="mb-4">
          “Cargo” es una compra que aumenta lo que debe. “Crédito a favor” proviene de pagos o notas de crédito y reduce la deuda. El saldo resultante indica la situación después de cada movimiento.
        </v-alert>

        <v-data-table :headers="movementHeaders" :items="movementDisplayRows" :loading="movementsLoading" item-key="id" dense>
          <template v-slot:item.movementDate="{ item }">{{ formatDate(item.movementDate) }}</template>
          <template v-slot:item.createdAt="{ item }">{{ formatDateTime(item.createdAt) }}</template>
          <template v-slot:item.document="{ item }">
            <div class="font-weight-medium">{{ item.document }}</div>
            <div v-if="item.detail" class="caption text--secondary">{{ item.detail }}</div>
          </template>
          <template v-slot:item.debit="{ item }">
            <span v-if="item.debit" class="blue--text text--darken-2 font-weight-medium">{{ formatAmount(item.debit) }}</span>
            <span v-else>—</span>
          </template>
          <template v-slot:item.credit="{ item }">
            <span v-if="item.credit" class="green--text text--darken-2 font-weight-medium">{{ formatAmount(item.credit) }}</span>
            <span v-else>—</span>
          </template>
          <template v-slot:item.runningBalance="{ item }">
            <span :class="balanceClass(item.runningBalance)">{{ balanceText(item.runningBalance) }}: {{ formatAmount(Math.abs(Number(item.runningBalance || 0))) }}</span>
          </template>
        </v-data-table>

        <v-expansion-panels flat class="mt-4">
          <v-expansion-panel>
            <v-expansion-panel-header class="font-weight-medium">
              Registrar ajuste manual excepcional
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-alert dense outlined type="warning">
                Usalo sólo para una corrección administrativa que no provenga de una factura, pago, nota de crédito o nota de débito. Esos comprobantes se registran automáticamente.
              </v-alert>
              <v-row dense>
                <v-col cols="12" md="3">
                  <v-select v-model="movementForm.movementType" :items="movementTypeOptions" item-text="text" item-value="value" label="Efecto" outlined dense />
                </v-col>
                <v-col cols="12" md="3"><v-text-field v-model.number="movementForm.amount" type="number" min="0.01" step="0.01" label="Importe" prefix="$" outlined dense /></v-col>
                <v-col cols="12" md="3"><v-text-field v-model="movementForm.movementDate" type="date" label="Fecha" outlined dense /></v-col>
                <v-col cols="12" md="3" class="d-flex align-end"><v-btn color="success" block :loading="movementSaving" @click="saveMovement">Registrar ajuste</v-btn></v-col>
                <v-col cols="12"><v-text-field v-model="movementForm.description" label="Motivo del ajuste" placeholder="Ej.: Corrección autorizada por diferencia de saldo" outlined dense /></v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <div v-if="selectedAccountType === 'supplier'" class="mt-4">
          <div class="subtitle-2 mb-2">Historial de pedidos/compras al proveedor</div>
          <v-data-table :headers="supplierPurchaseHeaders" :items="supplierPurchaseHistory" :loading="movementsLoading" item-key="id" dense>
            <template v-slot:item.status="{ item }">
              <span :class="['pill', supplierPurchaseStatusClass(item.status)]">{{ supplierPurchaseStatusText(item.status) }}</span>
            </template>
            <template v-slot:item.total="{ item }">{{ formatAmount(item.total) }}</template>
          </v-data-table>
        </div>
      </v-card-text>
    </v-card>

    <v-dialog v-model="historyDialog" max-width="1280px" scrollable @input="historyClosed">
      <v-card class="rounded-xl overflow-hidden customer-file">
        <v-card-title class="white--text d-flex justify-space-between align-center px-6 py-4 customer-file__header">
          <div class="d-flex align-center" style="gap:14px"><div class="customer-file__avatar"><v-icon dark>mdi-account-outline</v-icon></div><div><div class="headline font-weight-bold">Cuenta corriente del cliente</div><div class="body-2 customer-file__identity">{{ historyAccountLabel }}</div></div></div>
          <v-btn icon dark @click="historyDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pa-5 customer-file__body">
          <v-progress-linear v-if="historyLoading" indeterminate color="indigo" class="mb-4" />
          <v-alert v-if="historyError" type="error" dense outlined>{{ historyError }}</v-alert>
          <div class="customer-file__meta mb-4">
            <div class="customer-file__meta-items"><span><v-icon small color="primary" class="mr-1">mdi-card-account-details-outline</v-icon> CUIT: <strong>{{ historySelectedAccount && historySelectedAccount.taxId || '—' }}</strong></span><span><v-icon small color="primary" class="mr-1">mdi-tag-outline</v-icon> Lista: <strong>{{ historySelectedAccount && historySelectedAccount.priceList ? historySelectedAccount.priceList.name : '—' }}</strong></span></div>
            <v-btn v-if="historySelectedAccount" color="success" depressed small @click="openPaymentModal(historySelectedAccount)"><v-icon left small>mdi-cash-plus</v-icon>Registrar cobro</v-btn>
          </div>
          <v-alert v-if="historyError" type="error" dense outlined>{{ historyError }}</v-alert>
          <template v-if="!historyLoading && !historyError">
          <v-row dense class="mb-4">
            <v-col cols="12" sm="4"><div class="customer-metric customer-metric--balance"><div class="customer-metric__heading"><span>SALDO ACTUAL</span><v-icon color="primary" small>mdi-wallet-outline</v-icon></div><div :class="['customer-metric__value', balanceClass(history.account.balance)]">{{ balanceText(history.account.balance) }}: $ {{ formatAmount(Math.abs(Number(history.account.balance || 0))) }}</div><div class="customer-metric__hint">Situación operativa de la cuenta</div></div></v-col>
            <v-col cols="12" sm="4"><div class="customer-metric customer-metric--pending"><div class="customer-metric__heading"><span>COMPROBANTES PENDIENTES</span><v-icon color="orange darken-2" small>mdi-file-document-outline</v-icon></div><div class="customer-metric__value">{{ pendingHistoryInvoices.length }}<span v-if="(history.pendingInvoices || []).length > 500">+</span></div><div class="customer-metric__hint">Hasta 500 comprobantes consultados</div></div></v-col>
            <v-col cols="12" sm="4"><div class="customer-metric customer-metric--terms"><div class="customer-metric__heading"><span>CONDICIONES</span><v-icon color="teal darken-1" small>mdi-handshake-outline</v-icon></div><div class="customer-metric__value customer-metric__value--small">Límite $ {{ formatAmount(history.account.creditLimit) }}</div><div class="customer-metric__hint">Plazo: {{ history.account.paymentTermsDays || 0 }} días · {{ history.account.brokerName || 'Sin corredor' }}</div></div></v-col>
          </v-row>
          <div v-if="historySelectedAccount" class="customer-commercial mb-4">
            <div class="customer-commercial__header"><div><v-icon color="deep-purple" small class="mr-2">mdi-tag-multiple-outline</v-icon><strong>Condiciones comerciales</strong><div class="caption text--secondary mt-1">Datos configurados para este cliente</div></div><v-btn small outlined color="primary" @click="editRow(historySelectedAccount)"><v-icon left small>mdi-pencil-outline</v-icon>Editar datos</v-btn></div>
            <div class="customer-commercial__grid">
              <div><span>Bonificación habitual</span><strong>{{ historySelectedAccount.habitualBonusPercent == null ? 'No configurada' : `${formatQuantity(historySelectedAccount.habitualBonusPercent)} %` }}</strong><small>Condición del cliente</small></div>
              <div><span>Escala de bonificación del corredor</span><strong>{{ historySelectedAccount.brokerBonusScalePercent == null ? 'No configurada' : `${formatQuantity(historySelectedAccount.brokerBonusScalePercent)} %` }}</strong><small>Referencia para autorizar descuentos</small></div>
              <div><span>Comisión del vendedor</span><strong>{{ historySelectedAccount.sellerCommissionPercent == null ? 'No configurada' : `${formatQuantity(historySelectedAccount.sellerCommissionPercent)} %` }}</strong><small>No es un descuento al cliente</small></div>
            </div>
            <p class="caption text--secondary mb-0 mt-3">Las bonificaciones de artículos o listas se consultan en Precios → Bonificaciones y pueden depender del producto.</p>
          </div>
          <div v-if="pendingHistoryInvoices.length" class="customer-section mb-4"><div class="customer-section__heading"><div><v-icon color="orange darken-2" small class="mr-2">mdi-file-clock-outline</v-icon><strong>Comprobantes pendientes</strong></div><span class="customer-counter">{{ pendingHistoryInvoices.length }}{{ (history.pendingInvoices || []).length > 500 ? '+' : '' }}</span></div><v-simple-table dense class="customer-table"><thead><tr><th>Comprobante</th><th>Fecha</th><th class="text-right">Pendiente</th><th>Estado</th></tr></thead><tbody><tr v-for="invoice in pendingHistoryInvoices" :key="invoice.id"><td class="customer-document">{{ invoice.documentNumber }}</td><td>{{ formatDate(invoice.issueDate) }}</td><td class="text-right customer-amount">$ {{ formatAmount(invoice.outstandingAmount) }}</td><td><span class="customer-status" :class="invoice.status === 'partially_paid' ? 'customer-status--partial' : 'customer-status--pending'">{{ invoice.status === 'partially_paid' ? 'Pago parcial' : 'Pendiente' }}</span></td></tr></tbody></v-simple-table></div>
          <v-card v-if="Number(history.summary.openCount || 0) > 0" outlined class="mb-4 pa-3">
            <div class="subtitle-2 font-weight-bold mb-2">Antigüedad de comprobantes pendientes</div>
            <v-row dense><v-col v-for="bucket in agingBuckets" :key="bucket.label" cols="6" sm="4" md="2"><div class="caption text--secondary">{{ bucket.label }}</div><div class="font-weight-medium">$ {{ formatAmount(bucket.amount) }}</div></v-col></v-row>
          </v-card>
          <v-tabs v-model="historyTab" background-color="transparent" color="primary" show-arrows class="customer-tabs">
            <v-tab><v-icon left small>mdi-swap-vertical</v-icon>Movimientos</v-tab><v-tab><v-icon left small>mdi-clipboard-text-outline</v-icon>Pedidos</v-tab><v-tab><v-icon left small>mdi-file-document-outline</v-icon>Comprobantes</v-tab><v-tab><v-icon left small>mdi-receipt-text-outline</v-icon>Recibos</v-tab><v-tab><v-icon left small>mdi-link-variant</v-icon>Aplicaciones</v-tab>
          </v-tabs>
          <v-tabs-items v-model="historyTab" class="customer-tabs-content">
            <v-tab-item><div class="customer-section__heading mt-4"><div><v-icon color="primary" small class="mr-2">mdi-history</v-icon><strong>Movimientos actuales</strong></div></div><div class="caption text--secondary">El saldo resultante corresponde a la cuenta operativa luego de cada movimiento.</div>
              <v-data-table :headers="movementHeaders" :items="movementDisplayRows" :loading="movementsLoading" dense class="mt-2">
                <template v-slot:item.movementDate="{ item }">{{ formatDate(item.movementDate) }}</template>
                <template v-slot:item.createdAt="{ item }">{{ formatDateTime(item.createdAt) }}</template>
                <template v-slot:item.document="{ item }"><div class="font-weight-medium">{{ item.document }}</div><div v-if="receiptForMovement(item)" class="caption text--secondary">Recibo {{ receiptForMovement(item).receiptNumber }} <v-btn v-if="canDownloadReceipt && receiptForMovement(item).status === 'active'" text x-small color="primary" :loading="downloadingReceiptId === receiptForMovement(item).id" @click.stop="downloadReceipt(receiptForMovement(item))"><v-icon left x-small>mdi-file-pdf-box</v-icon>Descargar recibo</v-btn></div></template>
                <template v-slot:item.debit="{ item }"><span v-if="item.debit" class="customer-debit">$ {{ formatAmount(item.debit) }}</span><span v-else>—</span></template>
                <template v-slot:item.credit="{ item }"><span v-if="item.credit" class="customer-credit">$ {{ formatAmount(item.credit) }}</span><span v-else>—</span></template>
                <template v-slot:item.runningBalance="{ item }"><span class="customer-status" :class="Number(item.runningBalance) > 0 ? 'customer-status--pending' : Number(item.runningBalance) < 0 ? 'customer-status--positive' : 'customer-status--neutral'">{{ balanceText(item.runningBalance) }}: $ {{ formatAmount(Math.abs(Number(item.runningBalance || 0))) }}</span></template>
              </v-data-table>
              <v-expansion-panels flat class="customer-history-panel mt-4"><v-expansion-panel><v-expansion-panel-header><span><v-icon small color="primary" class="mr-2">mdi-archive-outline</v-icon><strong>Registros anteriores</strong> <span class="customer-history-note">· consulta, no incluidos en el saldo actual</span></span></v-expansion-panel-header><v-expansion-panel-content><v-data-table :headers="statementHeaders" :items="history.statement.filter(x => x.origin === 'FOX')" :loading="historyLoading" dense class="mt-2">
              <template v-slot:item.movementDate="{ item }">{{ formatDate(item.movementDate) }}</template><template v-slot:item.detail="{ item }"><div class="font-weight-medium">{{ item.documentNumber || item.description || item.movementLabel || historyDocumentType(item.referenceType) }}</div><div class="caption text--secondary">{{ item.movementLabel || historyStatus(item.status) }}</div></template><template v-slot:item.debit="{ item }"><span v-if="historyMovementSide(item) === 'debit'">$ {{ formatAmount(item.amount) }}</span><span v-else>—</span></template><template v-slot:item.credit="{ item }"><span v-if="historyMovementSide(item) === 'credit'" class="green--text text--darken-2">$ {{ formatAmount(item.amount) }}</span><span v-else>—</span></template><template v-slot:item.recordType="{ item }"><v-chip x-small outlined>{{ item.origin === 'FOX' ? 'Histórico' : 'Actual' }}</v-chip></template>
            </v-data-table></v-expansion-panel-content></v-expansion-panel></v-expansion-panels></v-tab-item>
            <v-tab-item><v-data-table :headers="orderHistoryHeaders" :items="historyOrders" :loading="historyLoading" dense class="mt-2">
              <template v-slot:item.issueDate="{ item }">{{ formatDate(item.issueDate) }}</template><template v-slot:item.status="{ item }"><v-chip x-small :color="item.cancelled ? 'red' : (item.creditApproved ? 'green' : 'orange')" dark>{{ historyStatus(item.status) }}</v-chip></template><template v-slot:item.creditApproved="{ item }">{{ item.creditApproved ? 'Aprobado' : (item.creditReviewed ? 'Revisado' : 'Sin revisar') }}</template><template v-slot:item.totalAmount="{ item }">$ {{ formatAmount(item.totalAmount) }}</template>
            </v-data-table></v-tab-item>
            <v-tab-item><v-data-table :headers="documentHistoryHeaders" :items="historyDocuments" :loading="historyLoading" dense class="mt-2">
              <template v-slot:item.documentType="{ item }">{{ historyDocumentType(item.documentType) }}</template><template v-slot:item.issueDate="{ item }">{{ formatDate(item.issueDate) }}</template><template v-slot:item.dueDate="{ item }">{{ formatDate(item.dueDate) }}</template><template v-slot:item.totalAmount="{ item }">$ {{ formatAmount(item.totalAmount) }}</template><template v-slot:item.outstandingAmount="{ item }">$ {{ formatAmount(item.outstandingAmount) }}</template><template v-slot:item.status="{ item }"><span class="customer-status" :class="statusClass(item.isVoided ? 'cancelled' : item.status)">{{ item.isVoided ? 'Anulado' : historyStatus(item.status) }}</span></template>
            </v-data-table></v-tab-item>
            <v-tab-item><v-data-table :headers="receiptHistoryHeaders" :items="history.receipts" :loading="historyLoading" dense class="mt-2">
              <template v-slot:item.paymentMethod="{ item }"><div>{{ item.paymentMethod || '—' }}</div><div v-if="item.paymentReferences" class="caption text--secondary">Ref. {{ item.paymentReferences }}</div><div v-if="item.checkNumber" class="caption text--secondary">Cheque {{ item.checkNumber }}{{ item.checkBank ? ` · ${item.checkBank}` : '' }}</div></template>
              <template v-slot:item.issueDate="{ item }">{{ formatDate(item.issueDate) }}</template><template v-slot:item.recordType="{ item }"><v-chip x-small outlined>{{ item.origin === 'FOX' ? 'Histórico' : 'Actual' }}</v-chip></template><template v-slot:item.amount="{ item }">$ {{ formatAmount(item.amount) }}</template><template v-slot:item.status="{ item }"><span class="customer-status" :class="statusClass(item.status)">{{ historyStatus(item.status) }}</span></template><template v-slot:item.actions="{ item }"><v-btn v-if="item.origin !== 'FOX' && item.status === 'active' && canDownloadReceipt" text small color="primary" :loading="downloadingReceiptId === item.id" @click="downloadReceipt(item)"><v-icon left small>mdi-file-pdf-box</v-icon>Descargar PDF</v-btn><span v-else class="caption text--secondary">—</span><v-btn v-if="item.origin !== 'FOX' && item.hasAttachment" text small color="teal" @click="downloadPaymentAttachment(item)"><v-icon left small>mdi-paperclip</v-icon>Ver comprobante de pago</v-btn><v-btn v-else-if="item.origin !== 'FOX' && item.status === 'active' && canAttachReceipt" text small color="primary" @click="openAttachment(item)"><v-icon left small>mdi-paperclip-plus</v-icon>Adjuntar pago</v-btn></template>
            </v-data-table></v-tab-item>
            <v-tab-item><v-data-table :headers="creditApplicationHeaders" :items="history.creditApplications || []" :loading="historyLoading" dense class="mt-2" no-data-text="No hay aplicaciones de saldo registradas">
              <template v-slot:item.createdAt="{ item }">{{ formatDate(item.createdAt) }}</template>
              <template v-slot:item.source="{ item }">{{ item.receiptNumber || item.creditNoteNumber || item.sourceDescription || 'Saldo de la cuenta' }}</template>
              <template v-slot:item.amount="{ item }"><div>$ {{ formatAmount(item.activeAmount == null ? item.amount : item.activeAmount) }}</div><div v-if="item.activeAmount != null && Number(item.activeAmount) < Number(item.amount)" class="caption text--secondary">Original: $ {{ formatAmount(item.amount) }}</div></template>
              <template v-slot:item.status="{ item }"><v-chip x-small :color="item.reversedAt || (item.activeAmount != null && Number(item.activeAmount) < Number(item.amount)) ? 'orange' : 'success'" dark>{{ item.reversedAt || (item.activeAmount != null && Number(item.activeAmount) <= 0) ? 'Revertida' : (item.activeAmount != null && Number(item.activeAmount) < Number(item.amount) ? 'Revertida en parte' : 'Aplicada') }}</v-chip><div v-if="item.reversalReason" class="caption">{{ item.reversalReason }}</div></template>
            </v-data-table></v-tab-item>
          </v-tabs-items>
          </template>
        </v-card-text>
        <v-card-actions class="px-6 pb-5 justify-space-between"><div class="caption text--secondary">Los registros anteriores son de consulta y no se suman al saldo operativo.</div><v-btn color="primary" text @click="historyDialog = false">Cerrar</v-btn></v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialogo Premium de Registrar Cobro en Cuenta Corriente -->
    <v-dialog v-model="paymentDialog" max-width="620px" persistent>
      <v-card class="rounded-xl overflow-hidden shadow-2xl payment-card">
        <!-- Encabezado Gradiente Sleek -->
        <v-card-title class="headline white--text d-flex justify-space-between align-center px-6 py-4" style="background: linear-gradient(135deg, #0d9488, #0f766e);">
          <div class="d-flex align-center">
            <v-icon dark left class="mr-2">mdi-cash-register</v-icon>
            <span class="font-weight-bold" style="letter-spacing: -0.01em;">{{ paymentAccountType === 'supplier' ? 'Registrar Pago' : 'Registrar Cobro' }}</span>
          </div>
          <v-btn icon dark small @click="paymentDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-6 mt-2">
          <!-- Información resumida del cliente y su saldo -->
          <div class="d-flex justify-space-between align-center mb-4 p-3 rounded-lg" style="background: var(--bg-card); border: 1px solid var(--border-subtle); padding: 12px; border-radius: 8px;">
            <div>
              <div class="caption text-uppercase text-secondary font-weight-bold" style="font-size: 10px; letter-spacing: 0.05em;">{{ paymentAccountType === 'supplier' ? 'Proveedor' : 'Cliente' }}</div>
              <div class="body-1 font-weight-bold primary--text">{{ paymentAccountName }}</div>
            </div>
            <div class="text-right">
              <div class="caption text-uppercase text-secondary font-weight-bold" style="font-size: 10px; letter-spacing: 0.05em;">
                {{ paymentAccountType === 'supplier' ? 'Saldo Adeudado' : 'Saldo Pendiente' }}
                <v-tooltip bottom max-width="250">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small color="blue-grey lighten-1" class="ml-1" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                  </template>
                  <span>Diferencia neta entre las compras a crédito y los pagos recibidos. Si es mayor a 0, representa una deuda activa.</span>
                </v-tooltip>
              </div>
              <div class="title font-weight-bold red--text text--darken-2">
                ${{ formatAmount(paymentAccountBalance) }}
              </div>
            </div>
          </div>

          <v-alert v-if="paymentActionError" type="error" dense outlined>{{ paymentActionError }}</v-alert>
          <v-alert v-if="pendingReceipt" type="warning" outlined dense>La respuesta del cobro no está confirmada. Cerrá este formulario y seleccioná «Recuperar cobro» para consultar el resultado sin generar otro.</v-alert>
          <v-form ref="paymentForm" lazy-validation>
            <v-row dense>
              <v-col cols="12">
                <v-text-field
                  v-model.number="paymentForm.amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  :label="paymentAccountType === 'supplier' ? 'Total a pagar' : 'Total recibido'"
                  outlined
                  dense
                  required
                  prefix="$"
                  :rules="[v => !!v || 'El monto es requerido', v => v > 0 || 'Debe ser mayor a 0']"
                >
                  <template v-slot:append>
                    <v-tooltip bottom max-width="250">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small color="blue-grey lighten-2" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                      </template>
                      <span>Monto total que el cliente está abonando en este momento para saldar o reducir su deuda de cuenta corriente.</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
              </v-col>

              <v-col cols="12">
                <v-select
                  v-model="paymentForm.paymentMethod"
                  :items="['Efectivo', 'Transferencia Bancaria', 'Tarjeta de Crédito', 'Tarjeta de Débito', 'Cheque']"
                  label="Medio de Pago"
                  outlined
                  dense
                  required
                  :rules="[v => !!v || 'Debe seleccionar un medio de pago']"
                >
                  <template v-slot:append>
                    <v-tooltip bottom max-width="250">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small color="blue-grey lighten-2" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                      </template>
                      <span>El canal financiero por el cual se recibe el pago del saldo corriente. Impactará directamente la caja diaria correspondiente.</span>
                    </v-tooltip>
                  </template>
                </v-select>
              </v-col>

              <template v-if="paymentAccountType === 'customer' && paymentMode === 'payment'">
                <v-col cols="12">
                  <v-select
                    v-model="paymentForm.treasuryAccountId"
                    :items="compatibleTreasuryAccounts"
                    item-text="label" item-value="id"
                    label="Cuenta que recibe el cobro"
                    hint="Indica dónde ingresará el dinero o valor recibido. Elegí la cuenta correspondiente al medio de pago." persistent-hint
                    outlined dense required
                    :rules="[v => !!v || 'Debe seleccionar una cuenta de tesorería']"
                  />
                </v-col>
                <v-col v-if="['Transferencia Bancaria', 'Tarjeta de Crédito', 'Tarjeta de Débito'].includes(paymentForm.paymentMethod)" cols="12"><v-text-field v-model.trim="paymentForm.paymentReference" label="Referencia o número de operación" outlined dense maxlength="120" hint="Podés usar el número de operación del comprobante recibido." persistent-hint /></v-col>
                <template v-if="paymentForm.paymentMethod === 'Cheque'">
                  <v-col cols="12" sm="6"><v-text-field v-model.trim="paymentForm.checkNumber" label="Número de cheque" outlined dense required :rules="[v => !!v || 'Número requerido']" /></v-col>
                  <v-col cols="12" sm="6"><v-text-field v-model.trim="paymentForm.checkBank" label="Banco" outlined dense /></v-col>
                  <v-col cols="12" sm="6"><v-text-field v-model="paymentForm.checkDate" type="date" label="Fecha de cobro / vencimiento" outlined dense /></v-col>
                  <v-col cols="12"><v-alert type="info" dense text>El cheque quedará pendiente en Tesorería y podrá registrarse como cobrado o rechazado manteniendo toda la trazabilidad.</v-alert></v-col>
                </template>
                <v-col cols="12" v-if="additionalPayments.length"><v-alert type="info" dense text>Del total recibido, $ {{ formatAmount(primaryPaymentAmount) }} corresponden al primer medio. El resto se distribuye abajo.</v-alert></v-col>
                <v-col cols="12" v-for="(part, index) in additionalPayments" :key="part.localId">
                  <v-card outlined class="pa-3 mb-2">
                    <div class="d-flex align-center mb-2"><strong>Otro medio de pago {{ index + 1 }}</strong><v-spacer /><v-btn icon small aria-label="Quitar medio de pago" @click="additionalPayments.splice(index, 1)"><v-icon small>mdi-close</v-icon></v-btn></div>
                    <v-row dense>
                      <v-col cols="12" sm="6"><v-select v-model="part.paymentMethod" :items="paymentMethods" label="Medio de pago" outlined dense @change="part.treasuryAccountId = ''" /></v-col>
                      <v-col cols="12" sm="6"><v-text-field v-model.number="part.amount" type="number" min="0.01" step="0.01" prefix="$" label="Importe" outlined dense /></v-col>
                      <v-col cols="12"><v-select v-model="part.treasuryAccountId" :items="accountsForPaymentMethod(part.paymentMethod)" item-text="label" item-value="id" label="Cuenta que recibe este importe" outlined dense /></v-col>
                      <template v-if="part.paymentMethod === 'Cheque'"><v-col cols="12" sm="6"><v-text-field v-model.trim="part.checkNumber" label="Número de cheque" outlined dense /></v-col><v-col cols="12" sm="6"><v-text-field v-model.trim="part.checkBank" label="Banco" outlined dense /></v-col><v-col cols="12"><v-text-field v-model="part.checkDate" type="date" label="Vencimiento" outlined dense /></v-col></template>
                      <v-col v-else-if="part.paymentMethod !== 'Efectivo'" cols="12"><v-text-field v-model.trim="part.reference" maxlength="120" label="Referencia de la operación" outlined dense /></v-col>
                    </v-row>
                  </v-card>
                </v-col>
                <v-col cols="12"><v-btn color="primary" text small @click="addPaymentPart"><v-icon small left>mdi-plus-circle-outline</v-icon>Agregar otro medio</v-btn></v-col>
              </template>

              <v-col cols="12">
                <v-text-field
                  v-model="paymentForm.paymentDate"
                  type="date"
                  label="Fecha de Cobro"
                  outlined
                  dense
                  required
                />
              </v-col>

              <v-col cols="12">
                <v-text-field
                  v-model="paymentForm.description"
                  label="Descripción / Observaciones"
                  placeholder="Ej: Pago de saldo remanente"
                  outlined
                  dense
                />
              </v-col>
            </v-row>
          </v-form>
          <template v-if="paymentAccountType === 'customer' && paymentMode === 'payment'">
            <v-file-input v-model="paymentAttachment" accept="application/pdf,image/jpeg,image/png" outlined dense show-size clearable prepend-icon="mdi-paperclip" label="Comprobante de pago (opcional)" hint="PDF, JPG o PNG, hasta 5 MB. Se guardará asociado al recibo." persistent-hint class="mb-4" />
            <div class="payment-allocation__heading"><div><v-icon color="primary" small class="mr-2">mdi-file-check-outline</v-icon><strong>Aplicar el cobro</strong></div><v-btn v-if="paymentAllocationManual" text small color="primary" @click="suggestPaymentAllocation">Restablecer propuesta</v-btn></div>
            <p class="caption text--secondary mt-1">{{ paymentAllocationManual ? 'Importes ajustados manualmente.' : 'Propuesta automática: se aplican primero los comprobantes más antiguos.' }} Podés cambiar cada importe antes de confirmar.</p>
            <v-progress-linear v-if="paymentPendingLoading" indeterminate color="primary" />
            <v-alert v-if="paymentPendingError" type="error" dense outlined>{{ paymentPendingError }}</v-alert>
            <v-alert v-else-if="!paymentPendingLoading && !paymentPendingInvoices.length" type="info" dense outlined>No hay comprobantes pendientes en la consulta. El importe del cobro quedará en la cuenta del cliente, pendiente de aplicar a un comprobante.</v-alert>
            <v-simple-table v-if="paymentPendingInvoices.length" dense class="payment-allocation__table"><thead><tr><th>Comprobante</th><th>Fecha</th><th>Pendiente</th><th>Aplicar $</th></tr></thead><tbody><tr v-for="invoice in paymentPendingInvoices" :key="invoice.id"><td>{{ invoice.documentNumber }}</td><td>{{ formatDate(invoice.issueDate) }}</td><td>$ {{ formatAmount(invoice.outstandingAmount) }}</td><td><v-text-field v-model.number="invoice.applyAmount" @input="paymentAllocationManual = true" type="number" min="0" :max="invoice.outstandingAmount" step="0.01" dense outlined hide-details class="my-1" style="max-width:150px" /></td></tr></tbody></v-simple-table>
            <div class="payment-allocation__summary mt-3"><div><span>Aplicado a comprobantes</span><strong>$ {{ formatAmount(paymentAppliedTotal) }}</strong></div><div><span>Importe que queda en cuenta</span><strong>$ {{ formatAmount(paymentUnapplied) }}</strong></div></div>
            <div class="caption text--secondary mt-2">Al confirmar, este importe quedará registrado en la cuenta del cliente para aplicarlo a otros comprobantes. El saldo general depende de todos sus movimientos.</div>
          </template>
        </v-card-text>

        <!-- Acciones Premium -->
        <v-card-actions class="px-6 pb-6 pt-0 d-flex justify-end gap-2" style="gap: 8px;">
          <v-btn text color="secondary" @click="paymentDialog = false">Cancelar</v-btn>
          <v-btn color="teal" dark class="px-6 rounded-lg font-weight-bold" :disabled="!!pendingReceipt" :loading="paymentSaving" @click="reviewPayment">
            <v-icon left>mdi-check-circle-outline</v-icon> {{ paymentAccountType === 'supplier' ? 'Revisar pago' : 'Revisar cobro' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="paymentConfirmation" max-width="620" scrollable>
      <v-card class="payment-review rounded-xl overflow-hidden">
        <v-card-title class="payment-review__header white--text">
          <v-icon dark left>mdi-clipboard-check-outline</v-icon>
          Revisar {{ paymentAccountType === 'supplier' ? 'pago' : 'cobro' }}
          <v-spacer /><v-btn icon dark aria-label="Volver al formulario" @click="paymentConfirmation=false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pa-5">
          <div class="payment-review__customer"><div class="caption text--secondary">{{ paymentAccountType === 'supplier' ? 'PROVEEDOR' : 'CLIENTE' }}</div><strong>{{ paymentAccountName }}</strong></div>
          <div class="payment-review__amount mt-4"><div><div class="caption">IMPORTE RECIBIDO</div><div class="payment-review__total">$ {{ formatAmount(paymentForm.amount) }}</div></div><v-icon large color="teal darken-1">mdi-cash-check</v-icon></div>
          <div class="payment-review__details mt-4"><div><span>Medio de pago</span><strong>{{ paymentForm.paymentMethod }}</strong></div><div v-if="paymentAccountType === 'customer' && paymentMode === 'payment'"><span>Cuenta receptora</span><strong>{{ compatibleTreasuryAccounts.find(x => x.id === paymentForm.treasuryAccountId)?.label || '—' }}</strong></div><div><span>Fecha</span><strong>{{ formatDate(paymentForm.paymentDate) }}</strong></div></div>
          <div v-if="additionalPayments.length" class="payment-review__section mt-4"><strong>Distribución del pago</strong><div class="mt-2">{{ paymentForm.paymentMethod }} · $ {{ formatAmount(primaryPaymentAmount) }}</div><div v-for="part in additionalPayments" :key="part.localId">{{ part.paymentMethod }} · $ {{ formatAmount(part.amount) }} · {{ accountsForPaymentMethod(part.paymentMethod).find(a => a.id === part.treasuryAccountId)?.label || '—' }}<span v-if="part.checkNumber"> · Cheque {{ part.checkNumber }}</span><span v-if="part.reference"> · Ref. {{ part.reference }}</span></div></div>
          <div v-if="paymentForm.paymentReference" class="payment-review__note mt-3"><v-icon small color="teal darken-1" class="mr-1">mdi-pound</v-icon>Referencia de la operación: {{ paymentForm.paymentReference }}</div>
          <div v-if="paymentAccountType === 'customer' && paymentMode === 'payment' && paymentForm.paymentMethod === 'Cheque'" class="payment-review__note mt-3"><v-icon small color="teal darken-1" class="mr-1">mdi-bank</v-icon>Cheque {{ paymentForm.checkNumber }}{{ paymentForm.checkBank ? ` · ${paymentForm.checkBank}` : '' }}{{ paymentForm.checkDate ? ` · Vence ${formatDate(paymentForm.checkDate)}` : '' }}</div>
          <template v-if="paymentAccountType === 'customer' && paymentMode === 'payment'">
            <div class="payment-review__section mt-5"><v-icon small color="primary" class="mr-1">mdi-file-document-check-outline</v-icon><strong>Aplicación a comprobantes</strong></div>
            <div v-if="!paymentPendingInvoices.some(x => Number(x.applyAmount) > 0)" class="caption text--secondary mt-2">No se aplicará a comprobantes en este cobro.</div>
            <div v-for="invoice in paymentPendingInvoices.filter(x => Number(x.applyAmount) > 0)" :key="invoice.id" class="payment-review__invoice"><div><strong>{{ invoice.documentNumber }}</strong><div class="caption text--secondary">Pendiente después: $ {{ formatAmount(Math.max(0, Number(invoice.outstandingAmount) - Number(invoice.applyAmount))) }}</div></div><div class="text-right"><strong>$ {{ formatAmount(invoice.applyAmount) }}</strong><div class="caption" :class="Number(invoice.applyAmount) >= Number(invoice.outstandingAmount) ? 'green--text text--darken-2' : 'orange--text text--darken-3'">{{ Number(invoice.applyAmount) >= Number(invoice.outstandingAmount) ? 'Quedará pagada' : 'Quedará parcialmente pagada' }}</div></div></div>
            <div class="payment-review__summary mt-3"><div><span>Total aplicado</span><strong>$ {{ formatAmount(paymentAppliedTotal) }}</strong></div><div><span>Importe que queda en cuenta</span><strong>$ {{ formatAmount(paymentUnapplied) }}</strong></div></div>
            <div v-if="paymentUnapplied > 0" class="payment-review__note mt-3"><v-icon small color="teal darken-1" class="mr-1">mdi-information-outline</v-icon>Al confirmar, este importe quedará registrado en la cuenta del cliente para aplicarlo a otros comprobantes. El saldo general depende de todos sus movimientos.</div>
          </template>
          <div v-if="paymentAttachment" class="payment-review__note mt-3"><v-icon small color="teal darken-1" class="mr-1">mdi-paperclip</v-icon>Comprobante a adjuntar: {{ paymentAttachment.name }}</div>
          <div class="caption text--secondary mt-4">{{ paymentAccountType === 'customer' && paymentMode === 'payment' ? 'Al confirmar se registrará el movimiento en la cuenta y en tesorería.' : 'Al confirmar se registrará el movimiento en la cuenta.' }}</div>
        </v-card-text>
        <v-card-actions class="px-5 pb-5"><v-btn text color="primary" @click="paymentConfirmation=false">Volver y editar</v-btn><v-spacer /><v-btn color="success" depressed :loading="paymentSaving" @click="submitPayment"><v-icon left small>mdi-check-circle-outline</v-icon>Confirmar {{ paymentAccountType === 'supplier' ? 'pago' : 'cobro' }}</v-btn></v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="attachmentDialog" max-width="520"><v-card><v-card-title>Adjuntar comprobante de pago</v-card-title><v-card-text><p v-if="attachmentReceipt">Recibo {{ attachmentReceipt.receiptNumber }}</p><v-alert v-if="attachmentError" type="error" outlined dense>{{ attachmentError }}</v-alert><v-file-input v-model="attachmentFile" accept="application/pdf,image/jpeg,image/png" label="Archivo" outlined dense show-size hint="PDF, JPG o PNG, hasta 5 MB" persistent-hint /></v-card-text><v-card-actions><v-spacer /><v-btn text @click="attachmentDialog=false">Cancelar</v-btn><v-btn color="primary" :loading="attachmentSaving" @click="saveAttachment">Adjuntar</v-btn></v-card-actions></v-card></v-dialog>
  </v-container>
</template>

<script>
import { currentAccountsService } from "../services/currentAccounts";
import { priceListsService } from "../services/priceLists";
import { purchasesService } from "../services/purchases";
import { receiptsService } from "../services/receipts";
import { treasuryService } from "../services/treasury";
import HelpHint from "@/components/HelpHint.vue";

function nullableText(v) {
  const s = String(v || "").trim();
  return s || null;
}

export default {
  name: "SalesCurrentAccounts",
  components: { HelpHint },
  props: {
    viewMode: {
      type: String,
      default: "customer",
    },
    collectionsOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      error: "",
      success: "",
      rows: [],
      selectedAccountId: "",
      selectedAccountLabel: "",
      selectedAccountType: "",
      selectedAccountBalance: 0,
      movements: [],
      historyDialog: false,
      historyLoading: false,
      downloadingReceiptId: "",
      paymentAttachment: null,
      attachmentDialog: false,
      attachmentReceipt: null,
      attachmentFile: null,
      attachmentSaving: false,
      attachmentError: "",
      historyError: "",
      historyTab: 0,
      historyAccountLabel: "",
      historySelectedAccount: null,
      history: { account: {}, summary: {}, aging: {}, openItems: [], documents: [], orders: [], receipts: [], statement: [] },
      supplierPurchaseHistory: [],
      movementsLoading: false,
      movementSaving: false,
      priceLists: [],
      treasuryAccounts: [],
      selectedSuggestion: null,
      suggestionText: "",
      customerSuggestions: [],
      suggestionsLoading: false,
      suggestionsError: false,
      suggestionTimer: null,
      suggestionRequest: 0,
      filters: {
        q: "",
        accountType: "all",
        priceListId: "",
        isActive: "all",
      },
      form: {
        id: "",
        code: "",
        accountType: "customer",
        clientType: "CM",
        name: "",
        tradeName: "",
        taxId: "",
        iibbProvince: "",
        brokerName: "",
        phone: "",
        email: "",
        address: "",
        deliveryAddress: "",
        contactTimeWindow: "",
        paymentCondition: "",
        priceListId: "",
        purchaseInvoiceType: "",
        creditLimit: 0,
        balance: 0,
        paymentTermsDays: 0,
        purchasedAtCounter: false,
        habitualBonusPercent: null,
        brokerBonusScalePercent: null,
        sellerCommissionPercent: null,
        notes: "",
        internalNotes: "",
        isActive: true,
      },
      reportLoading: false,
      report: {
        customers: { summary: { count: 0, totalBalance: 0 } },
        suppliers: { summary: { count: 0, totalBalance: 0 } },
        overall: { count: 0, totalBalance: 0 },
      },
      movementForm: {
        movementType: "debit",
        amount: null,
        description: "",
        treasuryAccountId: "",
        checkNumber: "",
        paymentReference: "",
        checkBank: "",
        checkDate: "",
        movementDate: new Date().toISOString().slice(0, 10),
      },
      movementHeaders: [
        { text: "Fecha del comprobante", value: "movementDate" },
        { text: "Registrado el", value: "createdAt" },
        { text: "Qué ocurrió", value: "document" },
        { text: "Cargo (+ deuda)", value: "debit", align: "end" },
        { text: "Crédito a favor", value: "credit", align: "end" },
        { text: "Saldo resultante", value: "runningBalance", align: "end" },
      ],
      openItemHeaders: [
        { text: "Comprobante", value: "documentType" }, { text: "Número", value: "documentNumber" }, { text: "Emisión", value: "issueDate" }, { text: "Vencimiento", value: "dueDate" }, { text: "Importe original", value: "originalAmount", align: "end" }, { text: "Pendiente", value: "outstandingAmount", align: "end" },
      ],
      statementHeaders: [
        { text: "Fecha", value: "movementDate" }, { text: "Comprobante / movimiento", value: "detail" }, { text: "Cargo", value: "debit", align: "end" }, { text: "Crédito", value: "credit", align: "end" }, { text: "Registro", value: "recordType" },
      ],
      orderHistoryHeaders: [
        { text: "Pedido", value: "orderNumber" }, { text: "Fecha", value: "issueDate" }, { text: "Estado", value: "status" }, { text: "Crédito", value: "creditApproved" }, { text: "Total", value: "totalAmount", align: "end" },
      ],
      documentHistoryHeaders: [
        { text: "Tipo", value: "documentType" }, { text: "Número", value: "documentNumber" }, { text: "Emisión", value: "issueDate" }, { text: "Vencimiento", value: "dueDate" }, { text: "Total", value: "totalAmount", align: "end" }, { text: "Pendiente", value: "outstandingAmount", align: "end" }, { text: "Estado", value: "status" },
      ],
      receiptHistoryHeaders: [
        { text: "Recibo", value: "receiptNumber" }, { text: "Fecha", value: "issueDate" }, { text: "Registro", value: "recordType" }, { text: "Medio", value: "paymentMethod" }, { text: "Importe", value: "amount", align: "end" }, { text: "Estado", value: "status" }, { text: "Comprobante", value: "actions", sortable: false },
      ],
      creditApplicationHeaders: [
        { text: "Fecha", value: "createdAt" }, { text: "Factura", value: "invoiceNumber" }, { text: "Origen del saldo", value: "source" }, { text: "Importe", value: "amount", align: "end" }, { text: "Estado", value: "status" },
      ],
      activeOptions: [
        { text: "Todos", value: "all" },
        { text: "Activas", value: true },
        { text: "Inactivas", value: false },
      ],
      accountTypeFilterOptions: [
        { text: "Todos", value: "all" },
        { text: "Clientes", value: "customer" },
        { text: "Proveedores", value: "supplier" },
      ],
      accountTypeOptions: [
        { text: "Cliente", value: "customer" },
        { text: "Proveedor", value: "supplier" },
      ],
      clientTypeOptions: [
        { text: "Mostrador (CM)", value: "CM" },
        { text: "Cuenta corriente (CC)", value: "CC" },
      ],
      purchaseInvoiceTypeOptions: [
        { text: "A", value: "A" },
        { text: "B", value: "B" },
        { text: "C", value: "C" },
      ],
      movementTypeOptions: [
        { text: "Débito (aumenta saldo)", value: "debit" },
        { text: "Crédito (reduce saldo)", value: "credit" },
      ],
      supplierPurchaseHeaders: [
        { text: "Número", value: "docNumber" },
        { text: "Fecha", value: "issueDate" },
        { text: "Estado", value: "status" },
        { text: "Total", value: "total" },
      ],
      paymentDialog: false,
      pendingReceipt: null,
      recoveringReceipt: false,
      paymentPendingLoading: false,
      paymentPendingError: "",
      paymentActionError: "",
      paymentPendingInvoices: [],
      additionalPayments: [],
      paymentMethods: ['Efectivo', 'Transferencia Bancaria', 'Tarjeta de Crédito', 'Tarjeta de Débito', 'Cheque'],
      paymentAllocationManual: false,
      paymentConfirmation: false,
      formDialog: false,
      paymentSaving: false,
      paymentAccountId: "",
      paymentAccountType: "customer",
      paymentAccountName: "",
      paymentAccountBalance: 0,
      paymentMode: "payment",
      paymentForm: {
        amount: null,
        paymentMethod: "Efectivo",
        paymentDate: new Date().toISOString().slice(0, 10),
        description: "",
      },
    };
  },
  computed: {
    canAttachReceipt() { return (this.$store.state.auth.user?.roles || []).some(role => String(role).toLowerCase() === 'admin') || (this.$store.state.auth.user?.permissions || []).includes('receipt.create'); },
    canDownloadReceipt() { return (this.$store.state.auth.user?.roles || []).some(role => String(role).toLowerCase() === 'admin'); },
    pendingHistoryInvoices() {
      return (this.history.pendingInvoices || []).slice(0, 500);
    },
    historyOrders() { return [...(this.history.nativeOrders || []).map(x => ({ ...x, creditApproved: x.creditStatus === "APPROVED", creditReviewed: x.creditStatus !== "PENDING" })), ...(this.history.orders || [])]; },
    historyDocuments() {
      const current = (this.history.nativeInvoices || []).map(x => ({ ...x, documentType: String(x.documentNumber || '').startsWith('N') ? 'Comprobante actual' : 'Factura', dueDate: null }));
      return [...current, ...(this.history.documents || [])];
    },
    paymentAppliedTotal() {
      return this.paymentPendingInvoices.reduce((sum, invoice) => sum + (Number(invoice.applyAmount) || 0), 0);
    },
    paymentUnapplied() { return Math.max(0, Number(this.paymentForm.amount || 0) - this.paymentAppliedTotal); },
    primaryPaymentAmount() { return Math.round((Number(this.paymentForm.amount || 0) - this.additionalPayments.reduce((sum, part) => sum + (Number(part.amount) || 0), 0)) * 100) / 100; },
    headers() {
      const hd = this.viewMode === "supplier" ? [
        { text: "Código", value: "code" }, { text: "Nombre", value: "name" }, { text: "CUIT", value: "taxId" },
        { text: "Estado", value: "isActive" }, { text: "Acciones", value: "actions", sortable: false }
      ] : [
        { text: "Código", value: "code" }, { text: "Cliente", value: "name" }, { text: "Saldo actual", value: "balance" },
        { text: "Corredor", value: "brokerName" }, { text: "Estado", value: "isActive" }, { text: "Acciones", value: "actions", sortable: false }
      ];
      return hd;
    },
    priceListOptions() {
      return this.priceLists.map((x) => ({ id: x.id, label: `${x.code} - ${x.name}` }));
    },
    compatibleTreasuryAccounts() {
      const typeByMethod = { Efectivo: 'cash', 'Transferencia Bancaria': 'bank', 'Tarjeta de Crédito': 'card', 'Tarjeta de Débito': 'card', Cheque: 'check' };
      const expected = typeByMethod[this.paymentForm.paymentMethod];
      return this.treasuryAccounts.filter((x) => x.isActive && (!expected || x.accountType === expected)).map((x) => ({ id: x.id, label: x.name || x.code }));
    },
    agingBuckets() {
      const aging = this.history.aging || {};
      return [
        { label: "A vencer", amount: aging.current },
        { label: "1 a 30 días", amount: aging.overdue1To30 },
        { label: "31 a 60 días", amount: aging.overdue31To60 },
        { label: "61 a 90 días", amount: aging.overdue61To90 },
        { label: "Más de 90 días", amount: aging.overdueMoreThan90 },
      ];
    },
    movementDisplayRows() {
      const signedTotal = this.movements.reduce((total, movement) => {
        const amount = Number(movement.amount || 0);
        return total + (movement.movementType === "debit" ? amount : -amount);
      }, 0);
      let runningBalance = Number(this.selectedAccountBalance || 0) - signedTotal;
      const chronological = [...this.movements].sort((a, b) => {
        const dateA = `${a.movementDate || ""} ${a.createdAt || ""}`;
        const dateB = `${b.movementDate || ""} ${b.createdAt || ""}`;
        return dateA.localeCompare(dateB);
      });
      return chronological.map((movement) => {
        const amount = Number(movement.amount || 0);
        runningBalance += movement.movementType === "debit" ? amount : -amount;
        return {
          ...movement,
          document: this.movementDocument(movement),
          detail: this.movementDetail(movement),
          debit: movement.movementType === "debit" ? amount : null,
          credit: movement.movementType === "credit" ? amount : null,
          runningBalance: Number(runningBalance.toFixed(2)),
        };
      }).reverse();
    },
  },
  created() {
    this.pendingReceipt = receiptsService.pendingCreate();
    if (this.viewMode === "supplier") {
      this.filters.accountType = "supplier";
      this.form.accountType = "supplier";
    } else if (this.viewMode === "customer") {
      this.filters.accountType = "customer";
      this.form.accountType = "customer";
    }
    this.loadAll();
  },
  beforeDestroy() { clearTimeout(this.suggestionTimer); this.suggestionRequest += 1; },
  watch: {
    suggestionText(value) {
      if (this.selectedSuggestion && value === this.selectedSuggestion.name) return;
      this.filters.q = String(value || '').trim();
      this.selectedSuggestion = null;
      this.scheduleSuggestions();
    },
    "filters.priceListId"() { this.scheduleSuggestions(); },
    "filters.isActive"() { this.scheduleSuggestions(); },
    "paymentForm.amount"() {
      if (this.paymentDialog && this.paymentAccountType === 'customer' && this.paymentMode === 'payment' && !this.paymentAllocationManual) this.suggestPaymentAllocation();
    },
    "paymentForm.paymentMethod"() {
      if (this.paymentAccountType !== "customer") return;
      const currentIsCompatible = this.compatibleTreasuryAccounts.some((account) => account.id === this.paymentForm.treasuryAccountId);
      if (!currentIsCompatible) this.paymentForm.treasuryAccountId = this.compatibleTreasuryAccounts[0]?.id || "";
    },
  },
  methods: {
    accountsForPaymentMethod(method) { const types = { Efectivo: 'cash', 'Transferencia Bancaria': 'bank', 'Tarjeta de Crédito': 'card', 'Tarjeta de Débito': 'card', Cheque: 'check' }; return this.treasuryAccounts.filter(a => a.accountType === types[method] && a.isActive).map(a => ({ id: a.id, label: a.name || a.code })); },
    addPaymentPart() { this.additionalPayments.push({ localId: `${Date.now()}-${Math.random()}`, paymentMethod: 'Efectivo', treasuryAccountId: '', amount: null, checkNumber: '', checkBank: '', checkDate: '', reference: '' }); },
    async recoverPendingReceipt() {
      this.recoveringReceipt = true; this.error = "";
      try {
        const receipt = this.unwrap(await receiptsService.retryPendingCreate());
        this.pendingReceipt = null;
        this.paymentDialog = false; this.paymentConfirmation = false;
        await this.loadAll();
        this.success = `Cobro recuperado: recibo ${receipt.docNumber}. Revisá si necesitás adjuntar el comprobante.`;
      } catch (error) { this.error = `No se pudo recuperar el cobro: ${this.getError(error)}`; this.pendingReceipt = receiptsService.pendingCreate(); }
      finally { this.recoveringReceipt = false; }
    },
    scheduleSuggestions() {
      clearTimeout(this.suggestionTimer);
      const request = ++this.suggestionRequest;
      const q = String(this.filters.q || '').trim();
      this.customerSuggestions = [];
      this.suggestionsError = false;
      this.suggestionsLoading = false;
      if (q.length < 2 || this.selectedSuggestion) return;
      this.suggestionTimer = setTimeout(async () => {
        this.suggestionsLoading = true;
        try {
          const params = { q, suggestions: true };
          if (this.filters.accountType !== 'all') params.accountType = this.filters.accountType;
          if (this.collectionsOnly) params.clientType = 'CC';
          if (this.filters.priceListId) params.priceListId = this.filters.priceListId;
          if (this.filters.isActive !== 'all') params.isActive = this.filters.isActive;
          const result = this.unwrap(await currentAccountsService.list(params)) || [];
          if (request === this.suggestionRequest) this.customerSuggestions = result;
        } catch (_) { if (request === this.suggestionRequest) this.suggestionsError = true; }
        finally { if (request === this.suggestionRequest) this.suggestionsLoading = false; }
      }, 300);
    },
    async chooseSuggestion(item) {
      if (!item) { this.filters.q = ''; this.suggestionText = ''; this.customerSuggestions = []; return; }
      clearTimeout(this.suggestionTimer); this.suggestionRequest += 1; this.suggestionsLoading = false;
      this.filters.q = item.code;
      await this.fetchRows();
      this.rows = this.rows.filter(row => row.id === item.id);
    },
    searchTyped() {
      if (!this.selectedSuggestion) this.filters.q = String(this.suggestionText || '').trim();
      this.fetchRows();
    },
    unwrap(response) {
      if (response && response.data && Object.prototype.hasOwnProperty.call(response.data, "data")) return response.data.data;
      return response && response.data ? response.data : null;
    },
    getError(error) {
      if (error && error.response && error.response.data) {
        if (error.response.data.error) return error.response.data.error;
        if (error.response.data.message) return error.response.data.message;
        if (error.response.data.errors) return String(error.response.data.errors);
      }
      if (error && error.message) return error.message;
      return "No pudimos completar la operación.";
    },
    async loadAll() {
      this.loading = true;
      this.error = "";
      this.success = "";
      try {
        const listsRes = await priceListsService.list();
        this.priceLists = this.unwrap(listsRes) || [];
        try {
          const treasuryRes = await treasuryService.listAccounts();
          this.treasuryAccounts = this.unwrap(treasuryRes) || [];
        } catch (_treasuryError) {
          // Un vendedor con acceso de sólo lectura a cuentas corrientes no debe
          // perder toda la pantalla por no tener permisos de Tesorería.
          this.treasuryAccounts = [];
        }
        await Promise.all([this.fetchRows(), this.fetchBalancesReport()]);
      } catch (error) {
        this.error = this.getError(error);
      } finally {
        this.loading = false;
      }
    },
    async fetchRows() {
      this.loading = true;
      this.error = "";
      this.success = "";
      try {
        const params = {};
        if (this.filters.q) params.q = this.filters.q;
        if (this.filters.accountType !== "all") params.accountType = this.filters.accountType;
        if (this.collectionsOnly) params.clientType = "CC";
        if (this.filters.priceListId) params.priceListId = this.filters.priceListId;
        if (this.filters.isActive !== "all") params.isActive = this.filters.isActive;
        const response = await currentAccountsService.list(params);
        this.rows = this.unwrap(response) || [];
        if (this.selectedAccountId) {
          const exists = this.rows.some((x) => x.id === this.selectedAccountId);
          if (!exists) {
            this.selectedAccountId = "";
            this.selectedAccountLabel = "";
            this.movements = [];
          }
        }
      } catch (error) {
        this.error = this.getError(error);
      } finally {
        this.loading = false;
      }
    },
    formatAmount(value) {
      const n = Number(value || 0);
      return Number.isFinite(n) ? n.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0,00";
    },
    formatDateTime(value) {
      if (!value) return "—";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleString("es-AR");
    },
    formatDate(value) {
      const raw = String(value || "");
      const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
      return match ? `${match[3]}/${match[2]}/${match[1]}` : "—";
    },
    isOverdue(value) {
      const raw = String(value || "").slice(0, 10);
      return /^\d{4}-\d{2}-\d{2}$/.test(raw) && raw < new Date().toISOString().slice(0, 10);
    },
    historyDocumentType(value) {
      const key = String(value || "").toUpperCase();
      const labels = { DVFACT: "Factura", DCCOBR: "Cobranza", DCCOBE: "Cobranza en efectivo", DVNCDT: "Nota de crédito", DCNDCH: "Cheque rechazado", DSINIC: "Saldo inicial", DCANTI: "Anticipo", INVOICE: "Factura", CREDIT_NOTE: "Nota de crédito", DEBIT_NOTE: "Nota de débito", RECEIPT: "Recibo", ORDER: "Pedido" };
      return labels[key] || value || "Movimiento";
    },
    historyStatus(value) {
      const key = String(value || "").toLowerCase();
      const labels = { issued: "Pendiente", partially_paid: "Pago parcial", open: "Pendiente", partial: "Pago parcial", paid: "Pagado", historical: "Histórico", active: "Activo", cancelled: "Cancelado", rejected: "Rechazado", completed: "Cumplido", fulfilled: "Cumplido", pending: "Pendiente", approved: "Aprobado", invoiced: "Facturado", partially_invoiced: "Facturado parcialmente" };
      return labels[key] || value || "—";
    },
    statusClass(value) {
      const status = String(value || '').toLowerCase();
      if (['paid', 'active', 'completed', 'approved', 'fulfilled'].includes(status)) return 'customer-status--positive';
      if (['cancelled', 'rejected', 'voided'].includes(status)) return 'customer-status--negative';
      if (['partial', 'partially_paid', 'partially_invoiced'].includes(status)) return 'customer-status--partial';
      if (['issued', 'open', 'pending'].includes(status)) return 'customer-status--pending';
      return 'customer-status--neutral';
    },
    historyMovementSide(item) {
      return String(item.movementSide || "").toLowerCase() === "credit" ? "credit" : "debit";
    },
    historyClosed(open) { if (!open && this.selectedAccountType === "customer") { this.selectedAccountId = ""; this.movements = []; } },
    async openCommercialHistory(item) {
      this.historyDialog = true;
      this.historyTab = 0;
      this.selectedAccountId = item.id;
      this.selectedAccountLabel = `${item.code} - ${item.name}`;
      this.selectedAccountType = item.accountType || "customer";
      this.selectedAccountBalance = Number(item.balance || 0);
      this.movements = [];
      this.fetchMovements();
      this.historyLoading = true;
      this.historyError = "";
      this.historyAccountLabel = `${item.code} - ${item.name}`;
      this.historySelectedAccount = item;
      this.history = { account: { name: item.name }, summary: {}, aging: {}, openItems: [], documents: [], orders: [], receipts: [], statement: [] };
      try {
        const response = await currentAccountsService.commercialHistory(item.id, { limit: 300 });
        this.history = this.unwrap(response) || this.history;
      } catch (error) {
        this.historyError = this.getError(error);
      } finally {
        this.historyLoading = false;
      }
    },
    balanceText(value) {
      const amount = Number(value || 0);
      if (amount > 0) return "A pagar";
      if (amount < 0) return "A favor";
      return "Sin saldo";
    },
    formatQuantity(value) {
      const number = Number(value);
      return Number.isFinite(number) ? number.toLocaleString('es-AR', { maximumFractionDigits: 4 }) : '—';
    },
    balanceClass(value) {
      const amount = Number(value || 0);
      if (amount > 0) return "font-weight-bold orange--text text--darken-3";
      if (amount < 0) return "font-weight-bold green--text text--darken-2";
      return "font-weight-bold text--secondary";
    },
    openAttachment(receipt) { this.attachmentReceipt = receipt; this.attachmentFile = null; this.attachmentError = ''; this.attachmentDialog = true; },
    async saveAttachment() {
      if (!this.attachmentFile || this.attachmentFile.size > 5 * 1024 * 1024) { this.attachmentError = 'Elegí un PDF, JPG o PNG de hasta 5 MB.'; return; }
      this.attachmentSaving = true; this.attachmentError = '';
      try { await receiptsService.uploadAttachment(this.attachmentReceipt.id, this.attachmentFile); this.attachmentReceipt.hasAttachment = true; this.attachmentDialog = false; }
      catch (error) { this.attachmentError = this.getError(error); }
      finally { this.attachmentSaving = false; }
    },
    async downloadPaymentAttachment(receipt) {
      try { const response = await receiptsService.downloadAttachment(receipt.id); const url = URL.createObjectURL(response.data); const link = document.createElement('a'); link.href = url; const disposition = response.headers['content-disposition'] || ''; const match = disposition.match(/filename="([^"]+)"/); link.download = match ? match[1] : `Comprobante-${receipt.receiptNumber}`; document.body.appendChild(link); link.click(); link.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000); }
      catch (error) { this.historyError = this.getError(error); }
    },
    receiptForMovement(movement) {
      if (movement.referenceType !== 'receipt_collection' || !movement.referenceId) return null;
      return (this.history.receipts || []).find(receipt => receipt.origin !== 'FOX' && receipt.id === movement.referenceId) || null;
    },
    async downloadReceipt(receipt) {
      this.downloadingReceiptId = receipt.id;
      try {
        const response = await receiptsService.downloadPdf(receipt.id);
        const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a'); link.href = url; link.download = `Recibo-${receipt.receiptNumber}.pdf`;
        document.body.appendChild(link); link.click(); link.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
      } catch (error) { this.historyError = this.getError(error); }
      finally { this.downloadingReceiptId = ''; }
    },
    movementDocument(movement) {
      const description = String(movement.description || "").trim();
      if (description) return description;
      const labels = {
        invoice: "Factura",
        payment: "Pago recibido",
        refund: "Nota de crédito",
        refund_payment: "Reintegro de nota de crédito",
        refund_payment_cancelled: "El importe quedó a favor del cliente",
        debit_note: "Nota de débito",
        debit_note_price_difference: "ND por diferencia de precio",
        debit_note_late_interest: "ND por intereses",
        debit_note_expense_recovery: "ND por recupero de gastos",
        debit_note_exchange_difference: "ND por diferencia de cambio",
        credit_note_compensation: "Compensación de nota de crédito",
        receipt_collection: "Recibo de cobro",
        receipt_cancellation: "Anulación de recibo",
        check_rejection: "Cheque rechazado",
        supplier_payment: "Pago a proveedor",
        purchase_invoice: "Factura de proveedor",
        opening_balance: "Saldo inicial",
        manual_adjustment: "Ajuste manual",
      };
      return labels[movement.referenceType] || (movement.movementType === "debit" ? "Cargo" : "Crédito");
    },
    movementDetail(movement) {
      const labels = {
        invoice: "Factura emitida",
        payment: "Cobro registrado",
        refund: "Nota de crédito emitida",
        refund_payment: "Reintegro por nota de crédito",
        refund_payment_cancelled: "No se entregó dinero; el crédito permanece disponible para futuras compras",
        debit_note: "Nota de débito emitida",
        debit_note_price_difference: "Ajuste fiscal por diferencia de precio",
        debit_note_late_interest: "Intereses facturados mediante Nota de Débito",
        debit_note_expense_recovery: "Gastos recuperados mediante Nota de Débito",
        debit_note_exchange_difference: "Ajuste fiscal por diferencia de cambio",
        credit_note_compensation: "Nota de débito que reduce o cancela el crédito disponible",
        receipt_collection: "Cobro respaldado por recibo y movimiento de tesorería",
        receipt_cancellation: "El cobro fue revertido y la deuda volvió a quedar pendiente",
        check_rejection: "El cheque fue rechazado; se revirtió el cobro sin generar IVA por el capital",
        supplier_payment: "Pago registrado",
        purchase_invoice: "Compra registrada",
        opening_balance: "Saldo inicial de la cuenta",
        manual_adjustment: "Corrección administrativa",
      };
      const detail = labels[movement.referenceType] || "Movimiento de cuenta corriente";
      return detail === this.movementDocument(movement) ? "" : detail;
    },
    async fetchBalancesReport() {
      this.reportLoading = true;
      this.error = "";
      try {
        const params = { includeInactive: false, limit: 5000 };
        if (this.filters.q) params.q = this.filters.q;
        const response = await currentAccountsService.balancesReport(params);
        const data = this.unwrap(response) || {};
        this.report = data;
      } catch (error) {
        this.error = this.getError(error);
      } finally {
        this.reportLoading = false;
      }
    },
    async openMovements(item) {
      this.selectedAccountId = item.id;
      this.selectedAccountLabel = `${item.code} - ${item.name}`;
      this.selectedAccountType = item.accountType || "";
      this.selectedAccountBalance = Number(item.balance || 0);
      this.supplierPurchaseHistory = [];
      this.error = "";
      this.success = "";
      this.resetMovementForm();
      await this.fetchMovements();
    },
    async fetchMovements() {
      if (!this.selectedAccountId) return;
      this.movementsLoading = true;
      this.error = "";
      try {
        const [response, accountResponse] = await Promise.all([
          currentAccountsService.listMovements(this.selectedAccountId, { limit: 500 }),
          currentAccountsService.get(this.selectedAccountId),
        ]);
        this.movements = this.unwrap(response) || [];
        const account = this.unwrap(accountResponse);
        if (account) {
          this.selectedAccountBalance = Number(account.balance || 0);
          this.selectedAccountLabel = `${account.code} - ${account.name}`;
        }
        if (this.selectedAccountType === "supplier") {
          const purchasesRes = await purchasesService.list();
          const purchases = this.unwrap(purchasesRes) || [];
          this.supplierPurchaseHistory = purchases.filter((row) => String(row.supplierAccountId || "") === String(this.selectedAccountId));
        }
      } catch (error) {
        this.error = this.getError(error);
      } finally {
        this.movementsLoading = false;
      }
    },
    supplierPurchaseStatusText(status) {
      const map = {
        draft: "Borrador",
        purchase: "Pedido de compra",
        partial: "Recepción parcial",
        done: "Recibido",
        invoiced: "Facturado",
        cancelled: "Cancelado",
        issued: "Emitido",
      };
      return map[status] || status || "-";
    },
    supplierPurchaseStatusClass(status) {
      if (status === "done" || status === "invoiced") return "pill--success";
      if (status === "partial" || status === "purchase") return "pill--warn";
      if (status === "cancelled") return "pill--danger";
      return "pill--info";
    },
    async saveMovement() {
      if (!this.selectedAccountId) {
        this.error = "Selecciona una cuenta primero.";
        return;
      }
      this.movementSaving = true;
      this.error = "";
      this.success = "";
      try {
        const amount = Number(this.movementForm.amount || 0);
        if (!Number.isFinite(amount) || amount <= 0) throw new Error("Importe inválido.");
        const payload = {
          movementType: this.movementForm.movementType,
          amount,
          movementDate: nullableText(this.movementForm.movementDate),
          referenceType: "manual_adjustment",
          referenceId: null,
          description: nullableText(this.movementForm.description),
        };
        await currentAccountsService.createMovement(this.selectedAccountId, payload);
        this.success = "Movimiento registrado.";
        this.resetMovementForm();
        await Promise.all([this.fetchMovements(), this.fetchRows()]);
      } catch (error) {
        this.error = this.getError(error);
      } finally {
        this.movementSaving = false;
      }
    },
    buildPayload() {
      const code = String(this.form.code || "").trim();
      const name = String(this.form.name || "").trim();
      if (!code || !name) throw new Error("Código y nombre son requeridos.");
      return {
        code,
        name,
        accountType: this.form.accountType,
        clientType: this.form.clientType,
        tradeName: nullableText(this.form.tradeName),
        taxId: nullableText(this.form.taxId),
        iibbProvince: nullableText(this.form.iibbProvince),
        brokerName: nullableText(this.form.brokerName),
        phone: nullableText(this.form.phone),
        email: nullableText(this.form.email),
        address: nullableText(this.form.address),
        deliveryAddress: nullableText(this.form.deliveryAddress),
        contactTimeWindow: nullableText(this.form.contactTimeWindow),
        paymentCondition: nullableText(this.form.paymentCondition),
        priceListId: nullableText(this.form.priceListId),
        purchaseInvoiceType: nullableText(this.form.purchaseInvoiceType),
        creditLimit: Number(this.form.creditLimit || 0),
        balance: Number(this.form.balance || 0),
        paymentTermsDays: Math.max(0, Number(this.form.paymentTermsDays || 0)),
        purchasedAtCounter: Boolean(this.form.purchasedAtCounter),
        habitualBonusPercent: this.form.habitualBonusPercent === null || this.form.habitualBonusPercent === "" ? null : Number(this.form.habitualBonusPercent),
        brokerBonusScalePercent: this.form.brokerBonusScalePercent === null || this.form.brokerBonusScalePercent === "" ? null : Number(this.form.brokerBonusScalePercent),
        sellerCommissionPercent: this.form.sellerCommissionPercent === null || this.form.sellerCommissionPercent === "" ? null : Number(this.form.sellerCommissionPercent),
        notes: nullableText(this.form.notes),
        internalNotes: nullableText(this.form.internalNotes),
        isActive: Boolean(this.form.isActive),
      };
    },
    async saveRow() {
      this.saving = true;
      this.error = "";
      this.success = "";
      try {
        const payload = this.buildPayload();
        if (this.form.id) {
          await currentAccountsService.update(this.form.id, payload);
          this.success = "Cuenta corriente actualizada.";
        } else {
          await currentAccountsService.create(payload);
          this.success = "Cuenta corriente creada.";
        }
        this.resetForm();
        this.formDialog = false;
        await Promise.all([this.fetchRows(), this.fetchBalancesReport()]);
        if (this.historyDialog && this.historySelectedAccount) {
          const updated = this.rows.find(row => row.id === this.historySelectedAccount.id);
          if (updated) await this.openCommercialHistory(updated);
        }
      } catch (error) {
        this.error = this.getError(error);
      } finally {
        this.saving = false;
      }
    },
    editRow(item) {
      this.form = {
        id: item.id,
        code: item.code || "",
        accountType: item.accountType || "customer",
        clientType: item.clientType || "CM",
        name: item.name || "",
        tradeName: item.tradeName || "",
        taxId: item.taxId || "",
        iibbProvince: item.iibbProvince || "",
        brokerName: item.brokerName || "",
        phone: item.phone || "",
        email: item.email || "",
        address: item.address || "",
        deliveryAddress: item.deliveryAddress || "",
        contactTimeWindow: item.contactTimeWindow || "",
        paymentCondition: item.paymentCondition || "",
        priceListId: item.priceListId || "",
        purchaseInvoiceType: item.purchaseInvoiceType || "",
        creditLimit: Number(item.creditLimit || 0),
        balance: Number(item.balance || 0),
        paymentTermsDays: Number(item.paymentTermsDays || 0),
        purchasedAtCounter: Boolean(item.purchasedAtCounter),
        habitualBonusPercent: item.habitualBonusPercent === null || item.habitualBonusPercent === undefined ? null : Number(item.habitualBonusPercent),
        brokerBonusScalePercent: item.brokerBonusScalePercent === null || item.brokerBonusScalePercent === undefined ? null : Number(item.brokerBonusScalePercent),
        sellerCommissionPercent: item.sellerCommissionPercent === null || item.sellerCommissionPercent === undefined ? null : Number(item.sellerCommissionPercent),
        notes: item.notes || "",
        internalNotes: item.internalNotes || "",
        isActive: Boolean(item.isActive),
      };
      this.openMovements(item);
      this.formDialog = true;
    },
    resetForm() {
      this.form = {
        id: "",
        code: "",
        accountType: this.viewMode === "supplier" ? "supplier" : "customer",
        clientType: "CM",
        name: "",
        tradeName: "",
        taxId: "",
        iibbProvince: "",
        brokerName: "",
        phone: "",
        email: "",
        address: "",
        deliveryAddress: "",
        contactTimeWindow: "",
        paymentCondition: "",
        priceListId: "",
        purchaseInvoiceType: "",
        creditLimit: 0,
        balance: 0,
        paymentTermsDays: 0,
        purchasedAtCounter: false,
        habitualBonusPercent: null,
        brokerBonusScalePercent: null,
        sellerCommissionPercent: null,
        notes: "",
        internalNotes: "",
        isActive: true,
      };
    },
    resetMovementForm() {
      this.movementForm = {
        movementType: "debit",
        amount: null,
        description: "",
        movementDate: new Date().toISOString().slice(0, 10),
      };
    },
    suggestPaymentAllocation() {
      let remaining = Math.max(0, Math.round((Number(this.paymentForm.amount) || 0) * 100));
      this.paymentPendingInvoices.forEach(invoice => {
        const pending = Math.max(0, Math.round(Number(invoice.outstandingAmount || 0) * 100));
        const applied = Math.min(remaining, pending);
        invoice.applyAmount = applied / 100;
        remaining -= applied;
      });
      this.paymentAllocationManual = false;
    },
    async openPaymentModal(item) {
      this.paymentAccountId = item.id;
      this.paymentAccountType = item.accountType;
      this.paymentAccountName = item.name;
      this.paymentAccountBalance = Number(item.balance || 0);
      this.paymentMode = "payment";
      this.paymentForm = {
        amount: null,
        paymentMethod: "Efectivo",
        paymentDate: new Date().toISOString().slice(0, 10),
        description: item.accountType === "supplier" ? "Pago de saldo a proveedor" : "Cobro parcial de saldo",
        treasuryAccountId: "",
        checkNumber: "",
        checkBank: "",
        checkDate: new Date().toISOString().slice(0, 10),
      };
      if (item.accountType === 'customer') {
        const firstCash = this.treasuryAccounts.find((account) => account.accountType === 'cash' && account.isActive);
        this.paymentForm.treasuryAccountId = firstCash ? firstCash.id : '';
      }
      this.paymentPendingInvoices = [];
      this.additionalPayments = [];
      this.paymentAttachment = null;
      this.paymentAllocationManual = false;
      this.paymentPendingError = "";
      this.paymentActionError = "";
      this.paymentDialog = true;
      if (item.accountType === 'customer') {
        this.paymentPendingLoading = true;
        try {
          const data = this.unwrap(await currentAccountsService.commercialHistory(item.id, { limit: 500 }));
          if ((data.pendingInvoices || []).length > 500) throw new Error('Hay más de 500 comprobantes pendientes; consultá con administración antes de registrar este cobro.');
          this.paymentPendingInvoices = (data.pendingInvoices || []).map(x => ({ ...x, applyAmount: 0 }));
          this.suggestPaymentAllocation();
        } catch (error) { this.paymentPendingError = this.getError(error); }
        finally { this.paymentPendingLoading = false; }
      }
      this.$nextTick(() => {
        if (this.$refs.paymentForm) {
          this.$refs.paymentForm.resetValidation();
        }
      });
    },
    reviewPayment() {
      if (!this.$refs.paymentForm.validate()) return;
      if (this.paymentAccountType === 'customer' && this.paymentMode === 'payment') {
        if (!Number.isFinite(Number(this.paymentForm.amount)) || Math.abs(Number(this.paymentForm.amount) * 100 - Math.round(Number(this.paymentForm.amount) * 100)) > 0.000001) { this.paymentActionError = 'El total debe tener hasta dos decimales.'; return; }
        if (this.paymentAttachment && this.paymentAttachment.size > 5 * 1024 * 1024) { this.paymentActionError = 'El comprobante supera 5 MB.'; return; }
        if (this.paymentPendingLoading || this.paymentPendingError) { this.paymentActionError = 'No se pudieron consultar los comprobantes pendientes.'; return; }
        if (this.additionalPayments.length) {
          const invalidPart = this.additionalPayments.some(part => !part.paymentMethod || !this.accountsForPaymentMethod(part.paymentMethod).some(a => a.id === part.treasuryAccountId) || !Number.isFinite(Number(part.amount)) || Number(part.amount) <= 0 || Math.abs(Number(part.amount) * 100 - Math.round(Number(part.amount) * 100)) > 0.000001 || (part.paymentMethod === 'Cheque' && !part.checkNumber));
          if (invalidPart || this.primaryPaymentAmount <= 0) { this.paymentActionError = 'Revisá los medios: cada uno necesita una cuenta y un importe válido. El primer medio debe tener importe mayor a cero.'; return; }
        }
        const bad = this.paymentPendingInvoices.some(x => !Number.isFinite(Number(x.applyAmount)) || Number(x.applyAmount) < 0 || Number(x.applyAmount) > Number(x.outstandingAmount) || Math.abs(Number(x.applyAmount) * 100 - Math.round(Number(x.applyAmount) * 100)) > 0.000001);
        if (bad || Math.round(this.paymentAppliedTotal * 100) > Math.round(Number(this.paymentForm.amount) * 100)) { this.paymentActionError = 'Revisá los importes: no pueden superar lo pendiente ni el monto del cobro.'; return; }
      }
      this.paymentActionError = '';
      this.paymentConfirmation = true;
    },
    async submitPayment() {
      if (!this.$refs.paymentForm.validate()) return;
      this.paymentSaving = true;
      this.error = "";
      this.success = "";
      try {
        const amount = Number(this.paymentForm.amount);
        const isSupplier = this.paymentAccountType === "supplier";
        if (this.paymentMode === "debt") {
          await currentAccountsService.payDebt(this.paymentAccountId, {
            amount,
            paymentMethod: this.paymentForm.paymentMethod,
            description: this.paymentForm.description || "Saldar deuda"
          });
          this.success = `Deuda saldada con $${this.formatAmount(amount)} para ${this.paymentAccountName}.`;
        } else if (!isSupplier) {
          const receiptResponse = await receiptsService.create({
            currentAccountId: this.paymentAccountId,
            treasuryAccountId: this.paymentForm.treasuryAccountId,
            amount,
            paymentMethod: this.paymentForm.paymentMethod,
            concept: this.paymentForm.description || 'Cobro de saldo corriente',
            issueDate: nullableText(this.paymentForm.paymentDate),
            checkNumber: nullableText(this.paymentForm.checkNumber),
            paymentReference: nullableText(this.paymentForm.paymentReference),
            ...(this.additionalPayments.length ? { paymentValues: [{ treasuryAccountId: this.paymentForm.treasuryAccountId, paymentMethod: this.paymentForm.paymentMethod, amount: this.primaryPaymentAmount, reference: nullableText(this.paymentForm.paymentReference), checkNumber: nullableText(this.paymentForm.checkNumber), checkBank: nullableText(this.paymentForm.checkBank), checkDate: nullableText(this.paymentForm.checkDate) }, ...this.additionalPayments.map(part => ({ treasuryAccountId: part.treasuryAccountId, paymentMethod: part.paymentMethod, amount: Number(part.amount), reference: nullableText(part.reference), checkNumber: nullableText(part.checkNumber), checkBank: nullableText(part.checkBank), checkDate: nullableText(part.checkDate) }))] } : {}),
            checkBank: nullableText(this.paymentForm.checkBank),
            checkDate: nullableText(this.paymentForm.checkDate),
            invoiceApplications: this.paymentPendingInvoices.filter(x => Number(x.applyAmount) > 0).map(x => ({ invoiceId: x.id, amount: Number(x.applyAmount) })),
          });
          const createdReceipt = this.unwrap(receiptResponse);
          this.success = `Recibo ${createdReceipt?.docNumber || ''} por $${this.formatAmount(amount)} registrado para ${this.paymentAccountName}.`;
          if (this.paymentAttachment && createdReceipt?.id) {
            try { await receiptsService.uploadAttachment(createdReceipt.id, this.paymentAttachment); this.success += ' Comprobante adjuntado.'; }
            catch (_) { this.success += ' El cobro quedó registrado, pero no se pudo adjuntar el archivo. Buscá el recibo en la pestaña Recibos y adjuntalo allí; no vuelvas a registrar el cobro.'; }
          }
        } else {
          const payload = {
            movementType: "credit",
            amount,
            movementDate: nullableText(this.paymentForm.paymentDate),
            referenceType: "payment",
            referenceId: isSupplier ? `PAG-${Date.now()}` : `REC-${Date.now()}`,
            description: `${this.paymentForm.description || (isSupplier ? 'Pago de saldo corriente' : 'Cobro de saldo corriente')} (${this.paymentForm.paymentMethod})`,
          };
          await currentAccountsService.createMovement(this.paymentAccountId, payload);
          this.success = `Pago de $${this.formatAmount(amount)} registrado con éxito para ${this.paymentAccountName}.`;
        }
        const paymentResultMessage = this.success;
        this.paymentConfirmation = false;
        this.paymentDialog = false;
        await Promise.all([this.fetchRows(), this.fetchBalancesReport()]);
        this.success = paymentResultMessage;
        if (this.historyDialog && this.historySelectedAccount?.id === this.paymentAccountId) {
          const refreshed = this.rows.find(row => row.id === this.paymentAccountId) || this.historySelectedAccount;
          await this.openCommercialHistory(refreshed);
        }
        if (this.selectedAccountId === this.paymentAccountId) {
          await this.fetchMovements();
        }
      } catch (error) {
        this.paymentConfirmation = false;
        this.pendingReceipt = receiptsService.pendingCreate();
        this.paymentActionError = this.getError(error) + ' Revisá los recibos antes de intentar nuevamente si no recibiste respuesta.';
      } finally {
        this.paymentSaving = false;
      }
    },
    async openDepositModal(item) {
      const amount = window.prompt(`Ingreso a cuenta para ${item.name}\nMonto:`);
      if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) return;
      const method = window.prompt("Método de pago:", "Efectivo");
      if (!method) return;
      try {
        await currentAccountsService.createDeposit(item.id, {
          amount: Number(amount),
          paymentMethod: method,
          concept: "Ingreso a cuenta"
        });
        this.success = `Ingreso de $${amount} registrado para ${item.name}`;
        await this.fetchRows();
      } catch (error) {
        this.error = this.getError(error);
      }
    },
    openPayDebtModal(item) {
      this.additionalPayments = [];
      this.paymentAccountId = item.id;
      this.paymentAccountType = item.accountType;
      this.paymentAccountName = item.name;
      this.paymentAccountBalance = item.balance;
      this.paymentMode = "debt";
      this.paymentForm.amount = item.balance > 0 ? item.balance : null;
      this.paymentDialog = true;
    },
    openCreateDialog() {
      this.resetForm();
      this.formDialog = true;
    },
    closeFormDialog() {
      this.formDialog = false;
      this.resetForm();
    },
  },
};
</script>

<style scoped>
.page { padding: 0; }
.page-hero { background: var(--bg-card); border-radius: 16px; padding: 24px 28px; margin-bottom: 20px; display: flex; align-items: center; justify-content: space-between; gap: 12px; box-shadow: var(--shadow-md); border: 1px solid var(--border-subtle); }
.page-hero h2 { margin: 0 0 6px; font-size: 24px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.02em; }
.page-hero p { margin: 0; color: var(--text-secondary); font-size: 14px; line-height: 1.5; }
.page-card { background: var(--bg-card); border-radius: 16px; padding: 8px 8px 4px; box-shadow: var(--shadow-md); border: 1px solid var(--border-subtle); }
.actions-row { display: flex; gap: 10px; margin-top: 8px; }
.pill { border-radius: 999px; padding: 3px 10px; font-size: 12px; font-weight: 700; }
.pill--success { color: #065f46; background: #d1fae5; }
.pill--info { color: #1d4ed8; background: #dbeafe; }
.pill--warn { color: #991b1b; background: #fee2e2; }

.customer-file__header { background: linear-gradient(120deg, #1747bb, #2869ec); }
.customer-file__avatar { width: 44px; height: 44px; border: 1px solid rgba(255,255,255,.35); background: rgba(255,255,255,.17); border-radius: 14px; display: grid; place-items: center; flex-shrink: 0; }
.customer-file__identity { color: rgba(255,255,255,.9); margin-top: 2px; }
.customer-file__body { background: var(--bg-card); }
.customer-file__meta { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; padding: 12px 14px; border: 1px solid var(--border-subtle); border-radius: 12px; }
.customer-file__meta-items { display: flex; flex-wrap: wrap; gap: 8px 22px; font-size: 13px; }
.customer-commercial { padding: 16px; border: 1px solid #dedaf1; border-radius: 14px; background: linear-gradient(135deg, #f9f7ff, #f5f9ff); }
.customer-commercial__header { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.customer-commercial__grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 10px; margin-top: 12px; }
.customer-commercial__grid > div { display: flex; flex-direction: column; gap: 3px; background: var(--bg-card); border: 1px solid var(--border-subtle); border-radius: 10px; padding: 10px 12px; }
.customer-commercial__grid span, .customer-commercial__grid small { color: var(--text-secondary); font-size: 12px; }
.customer-commercial__grid strong { color: #5333ac; font-size: 17px; }
.customer-metric { height: 100%; min-height: 112px; border: 1px solid var(--border-subtle); border-radius: 14px; padding: 14px 16px; background: var(--bg-card); box-shadow: 0 2px 8px rgba(31,53,89,.06); border-top: 3px solid #3971ef; }
.customer-metric--pending { border-top-color: #e99822; }
.customer-metric--terms { border-top-color: #22a697; }
.customer-metric__heading { color: var(--text-secondary); display: flex; justify-content: space-between; align-items: center; gap: 8px; font-size: 11px; font-weight: 700; letter-spacing: .04em; }
.customer-metric__value { font-size: 21px; line-height: 1.3; font-weight: 700; color: var(--text-primary); margin-top: 8px; }
.customer-metric__value--small { font-size: 17px; }
.customer-metric__hint { color: var(--text-secondary); font-size: 12px; margin-top: 4px; }
.customer-section { border: 1px solid var(--border-subtle); border-radius: 14px; overflow: hidden; background: var(--bg-card); }
.customer-section__heading { display: flex; align-items: center; justify-content: space-between; padding: 12px 14px; font-size: 14px; }
.customer-counter { background: #fff2df; color: #a85608; border-radius: 999px; padding: 2px 9px; font-size: 12px; font-weight: 700; }
.customer-document, .customer-amount { font-weight: 700; }
.customer-amount, .customer-debit { color: #2059bb; font-weight: 600; }
.customer-credit { color: #087951; font-weight: 600; }
.customer-status { display: inline-flex; align-items: center; border-radius: 999px; padding: 4px 10px; font-size: 11px; line-height: 1.2; font-weight: 700; white-space: nowrap; }
.customer-status--pending { background: #fff1da; color: #995000; }
.customer-status--partial { background: #e5efff; color: #204f9d; }
.customer-status--positive { background: #dbf5e8; color: #07613d; }
.customer-status--negative { background: #ffebe9; color: #9a312d; }
.customer-status--neutral { background: #edf1f6; color: #42526a; }
.customer-tabs { border-bottom: 1px solid var(--border-subtle); }
.customer-tabs-content { background: transparent !important; }
.customer-history-panel { border: 1px solid var(--border-subtle); border-radius: 12px; overflow: hidden; }
.customer-history-note { font-size: 12px; font-weight: 400; color: var(--text-secondary); }
@media (max-width: 600px) { .customer-file__meta { align-items: flex-start; } .customer-metric { min-height: auto; } .customer-history-note { display: block; margin-left: 28px; } }
.payment-allocation__heading { display: flex; align-items: center; justify-content: space-between; gap: 10px; color: var(--text-primary); }
.payment-allocation__table { border: 1px solid var(--border-subtle); border-radius: 12px; overflow: hidden; }
.payment-allocation__summary { display: flex; flex-wrap: wrap; gap: 10px; }
.payment-allocation__summary > div { flex: 1 1 180px; border: 1px solid var(--border-subtle); border-top: 3px solid #2871e9; border-radius: 10px; padding: 10px 12px; display: flex; flex-direction: column; font-size: 12px; color: var(--text-secondary); }
.payment-allocation__summary > div + div { border-top-color: #0d9a79; }
.payment-allocation__summary strong { font-size: 17px; color: var(--text-primary); margin-top: 3px; }

.payment-review__header { background: linear-gradient(120deg,#126d64,#119a85); font-size: 19px; }
.payment-review__customer { padding: 12px 14px; border: 1px solid var(--border-subtle); border-radius: 12px; }
.payment-review__amount { background: #e8f7f2; color: #075a44; border: 1px solid #c0e9d8; border-radius: 12px; padding: 14px 16px; display: flex; align-items: center; justify-content: space-between; }
.payment-review__total { font-size: 24px; font-weight: 700; line-height: 1.25; }
.payment-review__details { display: flex; gap: 12px; flex-wrap: wrap; }
.payment-review__details > div { flex: 1 1 130px; display: flex; flex-direction: column; font-size: 13px; }
.payment-review__details span, .payment-review__summary span { color: var(--text-secondary); font-size: 12px; }
.payment-review__section { color: var(--text-primary); font-size: 14px; }
.payment-review__invoice { display: flex; justify-content: space-between; gap: 10px; padding: 12px; margin-top: 8px; border: 1px solid var(--border-subtle); border-radius: 10px; }
.payment-review__summary { display: flex; flex-wrap: wrap; gap: 10px; }
.payment-review__summary > div { flex: 1 1 170px; display: flex; flex-direction: column; gap: 4px; padding: 10px 12px; border: 1px solid var(--border-subtle); border-top: 3px solid #2871e9; border-radius: 10px; }
.payment-review__summary > div + div { border-top-color: #0d9a79; }
.payment-review__note { border-radius: 10px; background: #e9f7f3; color: #155f50; padding: 10px 12px; font-size: 12px; }
@media (max-width: 960px) { .page-hero { flex-direction: column; align-items: flex-start; } }
</style>
